import { redirectToLogin } from "../services/httpClient";
import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  IconButton,
  InputAdornment,
  Popover,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Chip,
  ListItemText,
  OutlinedInput,
  Menu,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import AppointmentModal, { AppointmentData } from "../components/modals/AppointmentModal";
import { useNavigation } from "../contexts/RouterContext";
import { CalendarToday, Delete, FilterAltOff, EventNote, EventAvailable, EventBusy, EventRepeat, PersonAdd, List, Folder, Bolt, AssignmentInd, Close, Print, TableChart, UnfoldLess, UnfoldMore, Palette, Schedule as ScheduleIcon, ViewColumn } from '@mui/icons-material';
import ClearFiltersButton from '../components/ClearFiltersButton';
import { FaqButton } from "../components/FaqButton";
import QuickPatientRegisterModal from "../components/modals/QuickPatientRegisterModal";
import { QuickRegisterResult, patientsService } from "../services/patientsService";
import { schedulesService, mapScheduleError, ScheduleItem, ScheduleOptions, ScheduleSeriesItem, RescheduleSeriesPayload } from "../services/schedulesService";
import { holidaysService } from "../services/holidaysService";
import { scheduleBlocksService, mapScheduleBlockError, ScheduleBlockItem } from "../services/scheduleBlocksService";
import ScheduleBlockModal, { ScheduleBlockCreateData, ScheduleBlockUpdateData } from "../components/modals/ScheduleBlockModal";
import { useAuth } from "../contexts/AuthContext";
import { toLocalDateStr, formatDate } from "../utils/datetime";
import { Toast } from "../components/Toast";
import { useToast } from "../hooks/useToast";
import { useSchedulePermissions } from "../hooks/useSchedulePermissions";
import { colors, typography } from '../theme/designSystem';

interface MenuItemProps {
  label: string;
  href?: string;
  onClick?: (e: React.MouseEvent) => void;
}

interface UserSession {
  email: string;
  alias: string;
  clinicName: string;
  role: string;
  permissions: string[];
  menuItems: MenuItemProps[];
  loginTime: string;
}

interface ScheduleEvent {
  id: string;
  date: string;
  time: string;
  duration: number;
  patient: string;
  service: string;
  serviceTypeId: string | null;
  doctor: string;
  /** status canônico da tela: confirmed | pending | attended | cancelled | no_show */
  status: string;
  /** status_display original do agendamento (ex.: "Cancelado paciente") */
  statusDisplay: string | null;
  patientConfirmation: string | null;
  patientPresence: string | null;
  /** motivo do cancelamento (reason_to_cancellation) — exibido quando cancelado */
  reasonToCancellation: string | null;
  patientId: string | null;
  professionalId: string | null;
  teamName: string | null;
  roomId: string | null;
  roomName: string | null;
  notes: string | null;
  /** pago (pagamento efetuado) — agendamento pago não pode ser excluído */
  paid: boolean;
  /** data do pagamento (ISO); nula quando não pago */
  paymentDate: string | null;
  /** valor efetivamente pago — alerta de diferença pago × valor vigente */
  paymentFinalValue: number | null;
  paymentDiscount: number | null;
  /** valores GRAVADOS no agendamento (a edição exibe estes; recalculados no servidor
   *  quando muda profissional/equipe/serviço) */
  unitValue: number | null;
  unitDiscount: number | null;
  totalUnit: number | null;
  totalAll: number | null;
  /** série: total de ocorrências gravado (1 = único) */
  occurrences: number | null;
  occurrenceNumber: number | null;
  /** pacote (RN-228): tamanho do pacote do serviço; nulo quando não é pacote */
  packageSize: number | null;
  /** serviço de pacote sem série (herança do legado) */
  packageUnlinked: boolean;
  /** janela de edição vencida: somente leitura */
  locked: boolean;
  /** remarcações desta ocorrência */
  numReschedule: number;
}

// Data financeira exibida sem conversão de fuso: formata dd/MM/yyyy do próprio ISO.
const formatPaymentDate = (iso: string | null): string => {
  if (!iso) return '';
  const parts = iso.slice(0, 10).split('-');
  return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : '';
};

// Máximo de pílulas por célula (horário × dia) na visão de semana antes do "+N mais"
const WEEK_SLOT_MAX_PILLS = 5;

// Hachura do horário bloqueado (RN-118n), em vermelho claro como o botão de bloquear (pedido
// do Hilton, 30/09): marca sem esconder a grade nem os agendamentos
const BLOCK_HATCH = 'repeating-linear-gradient(135deg, rgba(227, 93, 106, 0.18) 0 5px, transparent 5px 11px)';
const BLOCK_RED = '#e35d6a';
// Vermelho do texto/selo sobre fundo claro ou branco (contraste >= 4.5:1; o BLOCK_RED não alcança)
const BLOCK_RED_STRONG = '#b3303f';
// Largura da faixa de bloqueios na visão do dia (só existe nos dias com bloqueio visível)
const DAY_BLOCK_LANE_PERCENT = 22;

// Visão escolhida pelo usuário (Dia/Semana/Mês × por horário/por profissional), lembrada neste
// navegador (decisão do Hilton, 30/09): a agenda volta como ele deixou
const SCHEDULE_VIEW_PREFS_KEY = 'clinic4us-schedule-view';
type CalendarView = 'month' | 'week' | 'day';
type ScheduleLayout = 'time' | 'professional';
const readScheduleViewPrefs = (): { view: CalendarView; layout: ScheduleLayout } => {
  try {
    const raw = JSON.parse(localStorage.getItem(SCHEDULE_VIEW_PREFS_KEY) ?? '{}');
    const layout: ScheduleLayout = raw.layout === 'professional' ? 'professional' : 'time';
    const view: CalendarView = ['month', 'week', 'day'].includes(raw.view) ? raw.view : 'day';
    // Por profissional não tem mês
    return { view: layout === 'professional' && view === 'month' ? 'week' : view, layout };
  } catch {
    return { view: 'day', layout: 'time' };
  }
};
// Linhas da grade (Dia, Semana, por profissional). Cada linha fecha uma hora cheia quando o
// horário termina em :30, por isso a mais forte vai nela; a meia hora é leve. Tons do design
// system (border/borderLight): os anteriores (#f1f3f4/#e9ecef) sumiam no branco (Hilton, 01/10/2026).
const GRID_LINE_HOUR = '1px solid #ced4da';
const GRID_LINE_HALF = '1px solid #dee2e6';
const GRID_COL = '#dee2e6';
const GRID_COL_GROUP = '#adb5bd';
// Visão por profissional: altura mínima do card para caber a faixa de status (horário + nome + status)
const PRO_STATUS_MIN_HEIGHT = 58;
// Visão por profissional: largura mínima de cada coluna (o que exceder rola na horizontal)
const PRO_COL_MIN_DAY = 168;
const PRO_COL_MIN_WEEK = 104;
// Meia hora da grade, igual às demais visões
const SLOT_PX = 45;
// Fora do horário de atendimento da clínica: hachura CINZA, em ângulo oposto ao do bloqueio
// (vermelho, 135°), para os dois não se confundirem quando se cruzam (decisão do Hilton, 01/10/2026,
// depois de testar cinza liso e linhas de borda). Translúcida: o realce sob o mouse continua aparecendo
const CLOSED_FILL = 'repeating-linear-gradient(45deg, rgba(108, 117, 125, 0.18) 0 2px, transparent 2px 10px)';

const timeToMinutesOf = (time: string) => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};


const capWord = (w: string): string => (w ? w.charAt(0).toLocaleUpperCase('pt-BR') + w.slice(1) : w);

// Nome das pílulas: COMPLETO, em Title Case (o cadastro tem nomes em CAIXA ALTA) e com as
// partículas em minúscula; onde a pílula não comporta, o CSS corta com reticências (pedido do Hilton, 01/10)
const NAME_PARTICLES = new Set(['da', 'das', 'de', 'do', 'dos', 'e']);
const patientDisplayName = (full: string): string => {
  const parts = (full || '').toLocaleLowerCase('pt-BR').split(/\s+/).filter(Boolean);
  if (parts.length === 0) return full;
  return parts.map((w, i) => (i > 0 && NAME_PARTICLES.has(w) ? w : capWord(w))).join(' ');
};

// Card de informações no hover (todas as visões): inclui confirmação, presença e o
// motivo quando cancelado
const eventTooltip = (e: ScheduleEvent): string =>
  `${e.time} - ${e.patient}\n`
  + `${e.service}${e.doctor ? ' - ' + e.doctor : ''}\n`
  + `Duração: ${e.duration}min\n`
  + `Confirmação: ${e.patientConfirmation || e.statusDisplay || '—'}\n`
  + `Presença: ${e.patientPresence || '—'}`
  + (e.reasonToCancellation && (e.patientConfirmation || e.statusDisplay || '').startsWith('Cancelado')
      ? `\nMotivo: ${e.reasonToCancellation}`
      : '')
  + (e.paid
      ? `\nPago${e.paymentDate ? ' em ' + formatPaymentDate(e.paymentDate) : ''} (não pode ser excluído)`
      : '');

/** Significado dos status pelo catálogo (RN-114d); os nomes históricos ficam como fallback. */
interface StatusKinds {
  attended: Set<string>;
  noShow: Set<string>;
  confirmed: Set<string>;
  cancelled: Set<string>;
}

const buildStatusKinds = (options: ScheduleOptions | null): StatusKinds => {
  const kinds: StatusKinds = {
    attended: new Set(['Compareceu']),
    noShow: new Set<string>(),
    confirmed: new Set(['Confirmado']),
    cancelled: new Set<string>(),
  };
  for (const s of [...(options?.presenceStatuses ?? []), ...(options?.confirmationStatuses ?? [])]) {
    if (s.kind === 'attended') kinds.attended.add(s.name);
    else if (s.kind === 'no_show') kinds.noShow.add(s.name);
    else if (s.kind === 'confirmed') kinds.confirmed.add(s.name);
    else if (s.kind?.startsWith('cancelled_')) kinds.cancelled.add(s.name);
  }
  return kinds;
};

/** Mapeia o agendamento real para o status canônico da tela (semântica RN-101/103, pelo kind — RN-114d) */
const mapScheduleStatus = (item: ScheduleItem, kinds: StatusKinds): string => {
  const display = item.statusDisplay ?? '';
  const presence = item.patientPresence ?? '';
  const confirmation = item.patientConfirmation ?? '';
  if (display.startsWith('Cancelado') || kinds.cancelled.has(display) || kinds.cancelled.has(confirmation)) return 'cancelled';
  if (presence.startsWith('Não compareceu') || kinds.noShow.has(presence)) return 'no_show';
  if (kinds.attended.has(presence) || kinds.attended.has(display)) return 'attended';
  if (kinds.confirmed.has(display)) return 'confirmed';
  return 'pending';
};

const mapScheduleItem = (item: ScheduleItem, kinds: StatusKinds): ScheduleEvent => {
  const start = new Date(item.dateTimeInitial);
  const end = new Date(item.dateTimeFinal);
  const minutes = Math.round((end.getTime() - start.getTime()) / 60000);
  return {
    id: item.id,
    date: item.dateTimeInitial.slice(0, 10),
    time: item.dateTimeInitial.slice(11, 16),
    duration: minutes > 0 ? minutes : 30,
    patient: item.patientName || item.subject || '(sem paciente)',
    service: item.serviceTypeName || item.subject || '',
    serviceTypeId: item.serviceTypeId,
    doctor: item.professionalName || '',
    status: mapScheduleStatus(item, kinds),
    statusDisplay: item.statusDisplay,
    patientConfirmation: item.patientConfirmation,
    patientPresence: item.patientPresence,
    reasonToCancellation: item.reasonToCancellation,
    patientId: item.patientId,
    professionalId: item.professionalId,
    teamName: item.teamName,
    roomId: item.roomId ?? null,
    roomName: item.roomName ?? null,
    notes: item.notes,
    paid: item.paid,
    paymentDate: item.paymentDate,
    paymentFinalValue: item.paymentFinalValue,
    paymentDiscount: item.paymentDiscount,
    unitValue: item.eventValueUnit,
    unitDiscount: item.eventValueUnitDiscount,
    totalUnit: item.eventValueTotalUnit,
    totalAll: item.eventValueTotalAll,
    occurrences: item.occurrences,
    occurrenceNumber: item.occurrenceNumber,
    packageSize: item.packageSize ?? null,
    packageUnlinked: item.packageUnlinked ?? false,
    locked: item.locked,
    numReschedule: item.numReschedule,
  };
};

/** Marca única de pacote (RN-228): "3/7" na série, "sem série" na avulsa de serviço de pacote, nula fora disso */
const packageMarkOf = (event: ScheduleEvent): string | null => {
  if (event.packageSize && (event.occurrences ?? 1) > 1) return `${event.occurrenceNumber ?? '?'}/${event.packageSize}`;
  if (event.packageUnlinked) return 'sem série';
  return null;
};

/** Pílula "Pacote 3/7" dos cards: fundo translúcido para ficar legível em cima de qualquer cor de status */
const PackageMark = ({ event, light = true, compact = false }: { event: ScheduleEvent; light?: boolean; compact?: boolean }) => {
  const mark = packageMarkOf(event);
  if (!mark) return null;
  const unlinked = event.packageUnlinked;
  return (
    <span
      title={unlinked ? 'Serviço de pacote sem série: sessão avulsa' : `Pacote: sessão ${mark}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 2, flexShrink: 0,
        padding: compact ? '0 4px' : '1px 6px', borderRadius: 10, fontSize: compact ? '0.62rem' : '0.68rem', fontWeight: 600, whiteSpace: 'nowrap',
        background: light ? 'rgba(255,255,255,0.28)' : (unlinked ? '#e2e3e5' : '#e0f5f8'),
        color: light ? 'white' : (unlinked ? '#41464b' : '#0b7285'),
      }}
    >
      <EventRepeat style={{ fontSize: compact ? 11 : 13 }} />
      {compact ? mark : `Pacote ${mark}`}
    </span>
  );
};

/** Item da visão do dia: agendamento ou bloqueio de agenda (RN-118n) no mesmo cálculo de colunas. */
type DayItem = ScheduleEvent & { block?: ScheduleBlockItem };

interface LayoutEvent extends ScheduleEvent {
  width: string;
  left: string;
  zIndex: number;
  top: string;
  height: string;
}

const ProfessionalSchedule: React.FC = () => {
  const [userSession, setUserSession] = useState<UserSession | null>(null);
  const { goToPatientRegister, goToPatients, goToDaySchedule } = useNavigation();
  const { user: authUser } = useAuth();

  // Combos reais da agenda (profissionais UEPR, tipos de serviço, equipes, status)
  const [scheduleOptions, setScheduleOptions] = useState<ScheduleOptions | null>(null);
  const statusKinds = useMemo(() => buildStatusKinds(scheduleOptions), [scheduleOptions]);
  // Profissionais inativos entram só sob demanda (consulta de histórico)
  const [showInactiveProfessionals, setShowInactiveProfessionals] = useState(false);

  // Default do filtro por escopo (RN-523): perfil 'own' começa só com o próprio profissional
  // marcado (os demais consultáveis sob demanda); 'entity' começa com todos os ATIVOS.
  // O filtro Equipe começa em "Todas as equipes" (decisão do Hilton, 25/09/2026): a equipe
  // principal deixou de vir pré-selecionada porque cliente com equipe por convênio via só
  // parte da agenda ao abrir.
  // Acessos defensivos (?? true / ?? []): durante um deploy a API pode responder o shape
  // anterior sem os campos novos — a tela degrada sem quebrar
  const defaultProfessionalNames = (options: ScheduleOptions) => {
    const base = options.dataScope === 'own'
      ? options.professionals.filter(p => options.ownProfessionalIds.includes(p.id))
      : options.professionals.filter(p => p.active ?? true);
    return base.map(p => p.name);
  };

  // Valores iniciais dos filtros ("Ir para data" começa em hoje, alinhado ao calendário)
  const getInitialFilters = () => ({
    team: '',
    startDate: toLocalDateStr(new Date()),
    professionals: scheduleOptions ? defaultProfessionalNames(scheduleOptions) : [],
    patients: [] as string[]
  });

  const [selectedProfessionals, setSelectedProfessionals] = useState<string[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedPatients, setSelectedPatients] = useState<string[]>([]);
  const [isPatientDropdownOpen, setIsPatientDropdownOpen] = useState(false);
  const [patientSearchTerm, setPatientSearchTerm] = useState('');
  const [hasFilterChanges, setHasFilterChanges] = useState(false);

  // Estados do calendário
  const [currentDate, setCurrentDate] = useState(new Date());
  // Abre sempre na visão do DIA (pedido do Hilton, 10/08/2026)
  const [calendarView, setCalendarView] = useState<CalendarView>(() => readScheduleViewPrefs().view);
  const [scheduleLayout, setScheduleLayout] = useState<ScheduleLayout>(() => readScheduleViewPrefs().layout);
  useEffect(() => {
    try {
      localStorage.setItem(SCHEDULE_VIEW_PREFS_KEY, JSON.stringify({ view: calendarView, layout: scheduleLayout }));
    } catch { /* sem storage: só nesta visita */ }
  }, [calendarView, scheduleLayout]);
  // Mês não existe na visão por profissional (inviável com muitas colunas): o botão some nela
  const proLayout = scheduleLayout === 'professional' && calendarView !== 'month';
  const changeScheduleLayout = (layout: ScheduleLayout) => {
    if (layout === 'professional' && calendarView === 'month') setCalendarView('week');
    setScheduleLayout(layout);
  };

  // Estados do modal de eventos
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [selectedDayEvents, setSelectedDayEvents] = useState<ScheduleEvent[]>([]);
  const [selectedDayDate, setSelectedDayDate] = useState<Date | null>(null);

  // Estado do evento selecionado para edição
  const [selectedEvent, setSelectedEvent] = useState<ScheduleEvent | null>(null);
  // Modo estrito do perfil 'own' (07/09/2026): agendamento de outro profissional abre só para leitura,
  // sem campos editáveis nem exclusão — o servidor já recusa, aqui a tela deixa de oferecer
  const isOwnDataScope = scheduleOptions?.dataScope === 'own';
  const isForeignEvent = !!(isOwnDataScope && selectedEvent && selectedEvent.professionalId
    && !(scheduleOptions?.ownProfessionalIds ?? []).includes(selectedEvent.professionalId));


  // Estados do modal unificado de agendamento
  const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState(false);
  const [appointmentModalMode, setAppointmentModalMode] = useState<'create' | 'edit'>('create');
  const [appointmentModalData, setAppointmentModalData] = useState<Partial<AppointmentData>>({});
  // Série da ocorrência em edição (diálogos de mover/excluir); vazia = único
  const [appointmentSeriesItems, setAppointmentSeriesItems] = useState<ScheduleSeriesItem[]>([]);
  const [appointmentSeriesPackage, setAppointmentSeriesPackage] = useState<{ size: number | null; chargeMode: 'total' | 'attended' | null } | null>(null);

  // Estados do modal de agenda do paciente
  const [isPatientScheduleModalOpen, setIsPatientScheduleModalOpen] = useState(false);
  const [patientScheduleEvents, setPatientScheduleEvents] = useState<ScheduleEvent[]>([]);


  // Estados dos filtros da agenda
  const [filters, setFilters] = useState(getInitialFilters());

  // Equipe selecionada no filtro e recortes derivados dela (RN-116):
  // - profissionais visíveis: ativos (+inativos sob demanda) e, com equipe, só os membros
  // - pacientes: quando a equipe tem pacientes atrelados, as buscas restringem a eles
  const selectedTeamFilter = filters.team
    ? (scheduleOptions?.teams ?? []).find(t => t.name === filters.team) ?? null
    : null;
  const teamPatientIdSet = selectedTeamFilter && (selectedTeamFilter.patientIds ?? []).length > 0
    ? new Set(selectedTeamFilter.patientIds)
    : null;
  const visibleProfessionals = (scheduleOptions?.professionals ?? [])
    .filter(p => (p.active ?? true) || showInactiveProfessionals)
    .filter(p => !selectedTeamFilter || (p.teamIds ?? []).includes(selectedTeamFilter.id));
  const professionalsList = visibleProfessionals.map(p => p.name);

  // Combos da agenda carregados uma vez (o refetch de eventos é por período/filtros)
  // Altura REAL do cabeçalho fixo (varia com zoom/breakpoint): ainda é necessária para a barra
  // de navegação presa — no celular sempre, e no desktop quando a agenda está recolhida.
  // A medição do título saiu em 22/09/2026 junto com o bloco preso, que voltou a rolar.
  useEffect(() => {
    const measure = () => {
      const header = document.querySelector<HTMLElement>('.header-internal');
      if (header) {
        document.documentElement.style.setProperty('--c4u-header-h', `${Math.ceil(header.getBoundingClientRect().height)}px`);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    // Remede quando o cabeçalho muda de tamanho (fontes carregando, hot reload, quebra de linha)
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    document.querySelectorAll<HTMLElement>('.header-internal').forEach((el) => observer?.observe(el));
    (document as Document & { fonts?: { ready: Promise<unknown> } }).fonts?.ready.then(measure);
    return () => {
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, []);

  useEffect(() => {
    schedulesService.getOptions()
      .then((options) => {
        setScheduleOptions(options);
        // Sem escolha do usuário ainda: seleção default conforme o escopo do perfil;
        // o filtro Equipe fica em "Todas as equipes"
        setSelectedProfessionals(defaultProfessionalNames(options));
      })
      .catch((error) => console.error('Erro ao carregar opções da agenda:', error));
  }, []);



  // Estados de controle
  const [isLoading, setIsLoading] = useState(false);
  // Item 132: bloco preso recolhido (só a barra de navegação) para ganhar espaço na grade; escolha guardada no navegador
  const [topCollapsed, setTopCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem('clinic4us-schedule-top-collapsed') === '1'; } catch { return false; }
  });
  // Item 126 (Lumiar): legenda das cores de confirmação e presença, num popover para não ocupar espaço
  const [legendAnchor, setLegendAnchor] = useState<HTMLElement | null>(null);
  const toggleTopCollapsed = () => {
    setTopCollapsed((v) => {
      try { localStorage.setItem('clinic4us-schedule-top-collapsed', v ? '0' : '1'); } catch { /* sem storage: só nesta visita */ }
      return !v;
    });
  };
  const [events, setEvents] = useState<ScheduleEvent[]>([]);
  // Bloqueios de agenda (RN-118n) da mesma janela e dos mesmos profissionais da agenda
  const [blocks, setBlocks] = useState<ScheduleBlockItem[]>([]);
  // Camada de bloqueios por cima dos agendamentos (decisão do Hilton, 01/10): começa ligada; quem
  // quer a grade limpa desliga. Os agendamentos nunca somem.
  const [showBlocks, setShowBlocks] = useState(true);
  // Visão por profissional: esconder as colunas de quem não tem agendamento no período (pedido do
  // Hilton, 02/10/2026). Começa mostrando todas; não é lembrado — é um filtro de momento.
  const [onlyWithAgenda, setOnlyWithAgenda] = useState(false);
  const [blockModal, setBlockModal] = useState<{ open: boolean; block: ScheduleBlockItem | null; date?: string; startTime?: string }>(
    { open: false, block: null });

  // Busca de pacientes reais (server-side) para o filtro — rótulo no formato da tela
  const [patientOptions, setPatientOptions] = useState<{ id: string; label: string }[]>([]);
  const patientsList = patientOptions.map(p => p.label);
  // Rótulo -> id acumulado entre buscas: um paciente selecionado continua resolvível
  // mesmo depois de o termo de busca (e o patientOptions) mudar
  const patientLabelIds = React.useRef<Record<string, string>>({});
  // Sequência da última busca da agenda (descartar respostas fora de ordem)
  const loadSeqRef = React.useRef(0);

  useEffect(() => {
    const term = patientSearchTerm.trim();
    if (term.length < 3) {
      setPatientOptions([]);
      return;
    }
    const timer = setTimeout(() => {
      patientsService.getAll({ pageNumber: 1, pageSize: 20, search: term, status: 'all', test: 'all' })
        .then((result) => {
          // Equipe com pacientes atrelados: a busca só mostra os pacientes da equipe
          const items = teamPatientIdSet
            ? result.items.filter(p => teamPatientIdSet.has(p.id))
            : result.items;
          const options = items.map(p => ({
            id: p.id,
            label: `${p.patientName} - CPF: ${p.patientDocument || 'não informado'} - Resp: ${p.responsibleName1 || 'não informado'}`,
          }));
          options.forEach(o => { patientLabelIds.current[o.label] = o.id; });
          setPatientOptions(options);
        })
        .catch((error) => console.error('Erro ao buscar pacientes:', error));
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientSearchTerm, filters.team, scheduleOptions]);

  useEffect(() => {
    document.title = "Clinic4Us - Agenda Profissional";

    // Verificar se há sessão ativa
    const sessionData = localStorage.getItem('clinic4us-user-session');
    if (sessionData) {
      try {
        const session = JSON.parse(sessionData);
        setUserSession(session);
      } catch (error) {
        console.error('Erro ao carregar sessão:', error);
        // Redirecionar para login se sessão inválida
        redirectToLogin();
      }
    } else {
      // Redirecionar para login se não há sessão
      redirectToLogin();
    }
  }, []);

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isPatientDropdownOpen) {
        setIsPatientDropdownOpen(false);
      }
    };

    if (isPatientDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isPatientDropdownOpen]);

  // Verificar se há mudanças nos filtros
  useEffect(() => {
    const initialFilters = getInitialFilters();

    const hasChanges =
      filters.team !== initialFilters.team ||
      selectedProfessionals.length !== initialFilters.professionals.length ||
      selectedProfessionals.some(p => !initialFilters.professionals.includes(p)) ||
      selectedPatients.length !== initialFilters.patients.length ||
      patientSearchTerm !== '';

    setHasFilterChanges(hasChanges);
  }, [filters, selectedProfessionals, selectedPatients, patientSearchTerm]);

  const handleRevalidateLogin = () => {
    // Limpar sessão
    localStorage.removeItem('clinic4us-user-session');
    localStorage.removeItem('clinic4us-remember-me');

    alert("Sessão encerrada. Redirecionando para login..");
    redirectToLogin();
  };

  const handleNotificationClick = () => {
    alert("Sistema de notificações - 27 notificações pendentes");
  };

  const handleUserClick = () => {
    alert("Menu do usuário:\n- Perfil\n- Configurações\n- Trocar senha\n- Logout");
  };

  const handleLogoClick = () => {
    // Redirecionar para dashboard
    const clinic = new URLSearchParams(window.location.search).get('clinic') || 'ninho';
    window.location.href = `${window.location.origin}/?page=dashboard&clinic=${clinic}`;
  };

  // Novo Paciente: menu com Cadastro rápido (modal reutilizável) e Cadastro completo (página)
  const [newPatientMenuAnchor, setNewPatientMenuAnchor] = useState<null | HTMLElement>(null);
  const [isQuickRegisterOpen, setIsQuickRegisterOpen] = useState(false);
  const { toast, showToast, hideToast } = useToast();

  const handleNewPatientClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setNewPatientMenuAnchor(e.currentTarget);
  };

  const handleFullRegisterClick = () => {
    setNewPatientMenuAnchor(null);
    const registerUrl = `${window.location.origin}${window.location.pathname}?page=patient-register`;
    window.open(registerUrl, '_blank');
  };

  const handleQuickRegisterSuccess = (result: QuickRegisterResult) => {
    if (result.emailSent) {
      showToast('Paciente cadastrado. Email de preenchimento enviado.', 'success');
    } else if (result.intakeLink) {
      showToast('Paciente cadastrado. O email não foi enviado neste ambiente — o link de preenchimento está disponível.', 'warning');
    } else {
      showToast('Paciente cadastrado. Complete o cadastro quando possível.', 'success');
    }
  };

  const handleProfessionalToggle = (professional: string) => {
    const newSelectedProfessionals = selectedProfessionals.includes(professional)
      ? selectedProfessionals.filter(p => p !== professional)
      : [...selectedProfessionals, professional];

    setSelectedProfessionals(newSelectedProfessionals);
    handleFilterChange('professionals', newSelectedProfessionals);
  };

  const handleSelectAllProfessionals = () => {
    const newSelectedProfessionals = selectedProfessionals.length === professionalsList.length
      ? []
      : [...professionalsList];

    setSelectedProfessionals(newSelectedProfessionals);
    handleFilterChange('professionals', newSelectedProfessionals);
  };

  const handlePatientToggle = (patient: string) => {
    const isCurrentlySelected = selectedPatients.includes(patient);

    const newSelectedPatients = selectedPatients.includes(patient) ? [] : [patient];

    setSelectedPatients(newSelectedPatients);

    // Fechar dropdown após seleção
    setIsPatientDropdownOpen(false);

    // Atualizar campo de busca
    setPatientSearchTerm(isCurrentlySelected ? '' : patient.split(' - ')[0]);

    // Atualizar filtros
    handleFilterChange('patients', newSelectedPatients);
  };


  const getFilteredPatients = () => {
    if (patientSearchTerm.length < 3) return [];
    return patientsList.filter(patient =>
      patient.toLowerCase().includes(patientSearchTerm.toLowerCase())
    );
  };

  const handlePatientSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Editar com paciente selecionado desfaz a seleção e recomeça a busca do zero
    // (antes o texto exibido era o do selecionado e o backspace não tinha efeito)
    if (selectedPatients.length > 0) {
      setSelectedPatients([]);
      handleFilterChange('patients', []);
    }

    setPatientSearchTerm(value);

    // Se o campo for limpo, remover seleção
    if (value === '') {
      setSelectedPatients([]);
      handleFilterChange('patients', []);
    }

    setIsPatientDropdownOpen(value.length >= 3);
  };

  // Botão X do campo de paciente: limpa seleção e busca de uma vez
  const clearPatientSelection = () => {
    setSelectedPatients([]);
    setPatientSearchTerm('');
    setIsPatientDropdownOpen(false);
    handleFilterChange('patients', []);
  };


  // Funções do calendário
  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    if (calendarView === 'month') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else if (calendarView === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setDate(newDate.getDate() - 1);
    }
    setCurrentDate(newDate);
  };

  const goToNext = () => {
    const newDate = new Date(currentDate);
    if (calendarView === 'month') {
      newDate.setMonth(newDate.getMonth() + 1);
    } else if (calendarView === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    setCurrentDate(newDate);
  };

  const getCalendarTitle = () => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long'
    };

    if (calendarView === 'day') {
      // O dia da semana vinha so na faixa dentro da grade, que saiu por repetir a data
      return currentDate.toLocaleDateString('pt-BR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } else if (calendarView === 'week') {
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);

      return `${startOfWeek.getDate()} - ${endOfWeek.getDate()} de ${startOfWeek.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}`;
    }

    return currentDate.toLocaleDateString('pt-BR', options);
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const current = new Date(startDate);

    for (let i = 0; i < 42; i++) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return days;
  };

  const getWeekDays = () => {
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());

    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      days.push(day);
    }

    return days;
  };

  // Funções do modal de eventos
  const openEventModal = (date: Date, events: ScheduleEvent[]) => {
    setSelectedDayDate(date);
    setSelectedDayEvents(events);
    setIsEventModalOpen(true);
  };

  const closeEventModal = () => {
    setIsEventModalOpen(false);
    setSelectedDayEvents([]);
    setSelectedDayDate(null);
  };

  // Funções do modal unificado de agendamento
  const openEditModal = (event: ScheduleEvent) => {
    setSelectedEvent(event);
    // Série da ocorrência: alimenta os diálogos de mover/excluir (melhor esforço)
    setAppointmentSeriesItems([]);
    setAppointmentSeriesPackage(null);
    if ((event.occurrences ?? 1) > 1) {
      schedulesService.getSeriesInfo(event.id)
        .then(info => { setAppointmentSeriesItems(info.items); setAppointmentSeriesPackage({ size: info.packageSize, chargeMode: info.packageChargeMode }); })
        .catch(() => setAppointmentSeriesItems([]));
    }
    const startDate = event.date;
    const endDate = event.date;

    // Edição espelha o que está GRAVADO: valores (nunca recalculados — o total de uma
    // série de 12 é 12x o unitário), série e status de confirmação/presença
    const money = (v: number | null): string => (v == null ? '' : v.toFixed(2));
    setAppointmentModalData({
      patient: event.patient,
      // id do paciente alimenta o atalho para o cadastro no modal (só leitura; a gravação
      // continua resolvendo os ids no onSave)
      patientId: event.patientId,
      firstResponsible: '',
      secondResponsible: '',
      professional: event.doctor,
      team: event.teamName || '',
      roomId: event.roomId ?? null,
      serviceType: event.service,
      serviceTypeId: event.serviceTypeId,
      startDate: startDate,
      startTime: event.time,
      endDate: endDate,
      endTime: endTimeFromDuration(event.time, event.duration),
      appointmentType: (event.occurrences ?? 1) > 1 ? 'recurring' : 'unique',
      recurrenceType: '',
      maxOccurrences: event.occurrences ?? 1,
      unitValue: money(event.unitValue),
      discount: money(event.unitDiscount ?? 0),
      totalUnit: money(event.totalUnit),
      totalValue: money(event.totalAll),
      observations: event.notes || '',
      confirmationStatus: event.patientConfirmation || '',
      attendanceStatus: event.patientPresence || '',
      cancellationReason: event.reasonToCancellation || ''
    });

    setAppointmentModalMode('edit');
    setIsAppointmentModalOpen(true);
    setIsEventModalOpen(false);
  };

  const schedulePerms = useSchedulePermissions();

  // Fora do atendimento (RN-118o) numa linha de 30 min, em Dia e Semana: mesmo dado das faixas
  // fechadas da visão por profissional. Sem horário cadastrado, nada é marcado; com cadastro, dia
  // sem faixa fica fechado inteiro. Sobreposição hachurada que não captura clique (agendar fora do
  // horário continua permitido)
  const isClosedSlot = (weekday: number, slotMin: number): boolean => {
    const hours = scheduleOptions?.businessHours ?? [];
    if (hours.length === 0) return false;
    return !hours.some(h => h.weekday === weekday && h.startMinute < slotMin + 30 && h.endMinute > slotMin);
  };
  // Mês não tem horas: o fechado ali é o dia da semana sem nenhuma faixa de atendimento
  const isClosedDay = (weekday: number): boolean => {
    const hours = scheduleOptions?.businessHours ?? [];
    return hours.length > 0 && !hours.some(h => h.weekday === weekday);
  };
  // A hachura entra como camada de fundo da própria célula: fica ABAIXO das pílulas (bloqueio e
  // agendamento continuam sólidos) e some junto quando a célula muda de cor sob o mouse
  const withClosedHatch = (closed: boolean, background: string) => closed ? `${CLOSED_FILL}, ${background}` : background;

  const openNewAppointmentModal = (date: Date, time?: string, professionalName?: string) => {
    // RN-115d: sem "inserir agendamento" o clique na grade não abre a criação
    if (!schedulePerms.canCreate) return;
    const dateStr = date.toISOString().split('T')[0];

    // Filtros com seleção única pré-preenchem o modal (profissional, paciente ou ambos);
    // a equipe entra pré-selecionada (a do filtro — default main_team) quando o
    // profissional pré-preenchido é membro dela
    const singleProfessional = professionalName
      ?? (selectedProfessionals.length === 1 ? selectedProfessionals[0] : '');
    const singlePatient = selectedPatients.length === 1 ? selectedPatients[0].split(' - ')[0] : '';
    const proOption = singleProfessional
      ? (scheduleOptions?.professionals ?? []).find(p => p.name === singleProfessional)
      : undefined;
    const teamPrefill = selectedTeamFilter && proOption && (proOption.teamIds ?? []).includes(selectedTeamFilter.id)
      ? selectedTeamFilter.name
      : '';

    setAppointmentModalData({
      patient: singlePatient,
      firstResponsible: '',
      secondResponsible: '',
      professional: singleProfessional,
      team: teamPrefill,
      serviceType: '',
      startDate: dateStr,
      startTime: time || '08:00',
      endDate: dateStr,
      endTime: time ? calculateEndTime(time) : '09:00',
      appointmentType: 'unique',
      recurrenceType: '',
      maxOccurrences: 1,
      unitValue: '',
      discount: '',
      totalUnit: '',
      totalValue: '',
      observations: ''
    });

    setAppointmentModalMode('create');
    setIsAppointmentModalOpen(true);
  };

  // "Agendar em seguida" (item 145, pedido da Lumiar): abre um agendamento NOVO encostado no que
  // acabou de ser gravado — mesmo paciente, profissional e dia, comecando onde o anterior terminou
  // e com a MESMA duracao.
  //
  // EQUIPE e SERVICO ficam em BRANCO de proposito (decisao do Hilton, 24/09/2026): quem escolhe e
  // o usuario, e a tela nao impoe regra sobre o que a segunda sessao pode ser. O caso que originou
  // isso e convenio + particular, e nesse cenario a equipe pode ser outra — o valor e por
  // (profissional x servico x EQUIPE), entao herdar a equipe do anterior traria o preco errado.
  //
  // A recorrencia do anterior e mantida: o cenario real e sessao semanal seguida de sessao
  // semanal. Vem preenchida e visivel, entao o usuario desfaz se nao quiser.
  const openSequelAppointmentModal = (anterior: AppointmentData) => {
    const duracaoMin = Math.max(
      0, timeToMinutes(anterior.endTime) - timeToMinutes(anterior.startTime));
    const inicio = anterior.endTime;
    const fimMin = timeToMinutes(inicio) + duracaoMin;
    const fim = `${Math.floor(fimMin / 60).toString().padStart(2, '0')}:${(fimMin % 60).toString().padStart(2, '0')}`;

    setAppointmentModalData({
      ...anterior,
      // Os ids vão junto com os nomes: o modal recompõe o serviço pelo id quando o nome não bate
      team: '',
      teamId: null,
      serviceType: '',
      serviceTypeId: null,
      startTime: inicio,
      endTime: fim,
      // Valores sao do servico: sem servico escolhido, nao ha o que exibir
      unitValue: '',
      discount: '',
      totalUnit: '',
      totalValue: '',
      observations: '',
    });
    setAppointmentModalMode('create');
    setIsAppointmentModalOpen(true);
  };

  const closeAppointmentModal = () => {
    setIsAppointmentModalOpen(false);
    setSelectedEvent(null);
    setAppointmentModalData({});
    setAppointmentSeriesItems([]);
  };

  // Navegação entre ocorrências da série DENTRO do modal: usa o evento da janela
  // carregada quando existe; fora dela, busca o registro pelo dia da ocorrência
  const openOccurrenceById = async (scheduleId: string, dateTimeInitial: string) => {
    let target = events.find(ev => ev.id === scheduleId);
    if (!target) {
      const date = dateTimeInitial.slice(0, 10);
      const result = await schedulesService.getSchedules({ from: date, to: date });
      const item = result.items.find(i => i.id === scheduleId);
      if (!item) {
        showToast('Não foi possível abrir o agendamento.', 'error');
        return;
      }
      target = mapScheduleItem(item, statusKinds);
    }
    openEditModal(target);
  };


  // Descontos do paciente para o modal (RN-115): resolve o id pelo nome exibido —
  // mapa da busca (label -> id) ou eventos já carregados — e consulta o endpoint escopado
  const loadPatientDiscountsByName = React.useCallback(async (patientName: string) => {
    const labelEntry = Object.entries(patientLabelIds.current)
      .find(([label]) => label.split(' - ')[0] === patientName);
    const fromEvents = events.find(ev => ev.patient === patientName)?.patientId;
    const patientId = labelEntry?.[1] ?? fromEvents ?? null;
    if (!patientId) return [];
    return schedulesService.getPatientDiscounts(patientId);
  }, [events]);

  // Responsáveis do paciente para o modal (RN-116): busca pelo nome na lista (escopada
  // pelo perfil) e devolve responsible_name_1/2 — campos somente leitura no modal
  const loadPatientInfoByName = React.useCallback(async (patientName: string) => {
    const result = await patientsService.getAll({
      pageNumber: 1, pageSize: 5, search: patientName, status: 'all', test: 'all',
    });
    // Só correspondência EXATA: texto parcial digitado não pode puxar dados de outro paciente
    const patient = result.items.find(p => p.patientName === patientName) ?? null;
    if (!patient) return null;
    return {
      responsible1: patient.responsibleName1 ?? null,
      responsible2: patient.responsibleName2 ?? null,
    };
  }, []);

  // Busca de pacientes DENTRO do modal (mesmo formato de rótulo da página); alimenta o
  // mapa rótulo->id para descontos e gravação resolverem o paciente selecionado
  const searchPatientsForModal = React.useCallback(async (term: string) => {
    // Paciente de teste (RN-552) É agendável: some dos relatórios, não da agenda
    const result = await patientsService.getAll({
      pageNumber: 1, pageSize: 20, search: term, status: 'all', test: 'all',
    });
    // Equipe do filtro com pacientes atrelados restringe também a busca do modal
    const items = teamPatientIdSet
      ? result.items.filter(p => teamPatientIdSet.has(p.id))
      : result.items;
    const options = items.map(p => ({
      id: p.id,
      label: `${p.patientName} - CPF: ${p.patientDocument || 'não informado'} - Resp: ${p.responsibleName1 || 'não informado'}`,
    }));
    options.forEach(o => { patientLabelIds.current[o.label] = o.id; });
    return options.map(o => o.label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.team, scheduleOptions]);

  // Feriados ativos na data escolhida (RN-407): alimenta o aviso não bloqueante do modal
  const loadHolidaysOnDate = React.useCallback(async (date: string) => {
    if (!authUser?.entityId) return [];
    const rows = await holidaysService.getOnDate(authUser.entityId, date);
    return rows.map(h => h.holidayTitle);
  }, [authUser?.entityId]);

  // Calendário completo de feriados ativos (data ISO → nome): sinaliza as prévias de
  // datas da recorrência/série no modal
  const loadHolidayCalendar = React.useCallback(async () => {
    if (!authUser?.entityId) return {};
    const rows = await holidaysService.getByEntity(authUser.entityId, false);
    return rows.reduce<Record<string, string>>((map, h) => {
      if (h.active) map[h.day] = h.holidayTitle;
      return map;
    }, {});
  }, [authUser?.entityId]);

  // Gravação real (fase B, RN-302): o modal aguarda a Promise — overlay aberto enquanto a
  // série inteira é escrita no Bubble + tenant; erro volta para o modal sem fechar
  const handleAppointmentSave = async (data: AppointmentData) => {
    if (appointmentModalMode === 'edit') {
      // Edição plena (menos paciente) dentro da janela; applyToSeries = este e os seguintes
      if (!selectedEvent) {
        return;
      }
      if (!data.professionalId || !data.serviceTypeId) {
        throw new Error('Selecione profissional e serviço.');
      }
      if (!data.startDate || !data.startTime || !data.endTime) {
        throw new Error('Informe a data e os horários do agendamento.');
      }
      try {
        const result = await schedulesService.updateSchedule(selectedEvent.id, {
          professionalRegistrationId: data.professionalId,
          teamId: data.teamId ?? null,
          serviceTypeId: data.serviceTypeId,
          startDate: data.startDate.slice(0, 10),
          startTime: data.startTime,
          endTime: data.endTime,
          confirmationStatus: data.confirmationStatus ? data.confirmationStatus : null,
          presenceStatus: data.attendanceStatus ? data.attendanceStatus : null,
          notes: data.observations ? data.observations : null,
          cancellationReason: data.cancellationReason ? data.cancellationReason : null,
          applyToSeries: data.applyToSeries ?? false,
          roomId: data.roomId ?? '',
        });
        showToast(result.updated > 1
          ? `${result.updated} agendamentos atualizados.`
          : 'Agendamento atualizado.', 'success');
        loadSchedules();
        // O modal fica aberto após salvar: rebaseia com o GRAVADO (busca pelo dia novo —
        // a lista da agenda pode ainda estar defasada) para zerar o dirty e atualizar série/histórico
        try {
          const day = data.startDate.slice(0, 10);
          const fresh = await schedulesService.getSchedules({ from: day, to: day });
          const item = fresh.items.find(i => i.id === selectedEvent.id);
          if (item) {
            openEditModal(mapScheduleItem(item, statusKinds));
          }
        } catch {
          // Sem a recarga o modal segue utilizável — só não rebaseia o formulário
        }
      } catch (error: any) {
        throw new Error(mapScheduleError(error));
      }
      return;
    }

    const labelEntry = Object.entries(patientLabelIds.current)
      .find(([label]) => label.split(' - ')[0] === data.patient);
    const patientId = labelEntry?.[1]
      ?? events.find(ev => ev.patient === data.patient)?.patientId
      ?? null;

    if (!patientId || !data.professionalId || !data.serviceTypeId) {
      throw new Error('Selecione paciente, profissional e serviço antes de agendar.');
    }
    if (!data.startDate || !data.startTime || !data.endTime) {
      throw new Error('Informe a data e os horários do agendamento.');
    }
    const recurring = data.appointmentType === 'recurring';
    if (recurring && !data.recurrenceType) {
      throw new Error('Escolha a periodicidade da recorrência.');
    }

    try {
      const result = await schedulesService.createSchedule({
        patientId,
        professionalRegistrationId: data.professionalId,
        teamId: data.teamId ?? null,
        serviceTypeId: data.serviceTypeId,
        startDate: data.startDate.slice(0, 10),
        startTime: data.startTime,
        endTime: data.endTime,
        recurring,
        recurrenceType: recurring ? data.recurrenceType : null,
        occurrences: recurring ? data.maxOccurrences : 1,
        notes: data.observations ? data.observations : null,
        confirmationStatus: data.confirmationStatus ? data.confirmationStatus : null,
        presenceStatus: data.attendanceStatus ? data.attendanceStatus : null,
        skippedDates: recurring ? data.skippedDates ?? [] : [],
        roomId: data.roomId ?? null,
        slots: recurring && data.slots && data.slots.length > 0 ? data.slots : undefined,
      });
      showToast(result.ids.length > 1
        ? `${result.ids.length} agendamentos gravados.`
        : 'Agendamento gravado.', 'success');
      loadSchedules();
    } catch (error: any) {
      throw new Error(mapScheduleError(error));
    }
  };

  // Exclusão lógica (fase B): motivo obrigatório; includeFollowing = série deste em diante
  const handleAppointmentDelete = async (
    opts: { reason: string; includeFollowing: boolean }) => {
    if (!selectedEvent) {
      return;
    }
    try {
      const result = await schedulesService.deleteSchedule(selectedEvent.id, opts);
      const kept = result.skippedProtected > 0
        ? ` ${result.skippedProtected} mantido(s) por status ou pagamento.`
        : '';
      showToast(result.deleted > 1
        ? `${result.deleted} agendamentos excluídos.${kept}`
        : `Agendamento excluído.${kept}`, 'success');
      loadSchedules();
    } catch (error: any) {
      throw new Error(mapScheduleError(error));
    }
  };

  // Lista da série editável (RN-118m): grava e recarrega a série e a agenda
  const handleRescheduleSeries = async (payload: RescheduleSeriesPayload) => {
    if (!selectedEvent) {
      return;
    }
    try {
      const result = await schedulesService.rescheduleSeries(selectedEvent.id, payload);
      const parts = [
        result.moved > 0 ? `${result.moved} ${result.moved === 1 ? 'sessão remanejada' : 'sessões remanejadas'}` : '',
        result.deleted > 0 ? `${result.deleted} ${result.deleted === 1 ? 'removida' : 'removidas'}` : '',
      ].filter(Boolean);
      showToast(`${parts.join(' e ')}.`.replace(/^./, c => c.toUpperCase()), 'success');
      setAppointmentSeriesItems(await schedulesService.getSeries(selectedEvent.id));
      loadSchedules();
    } catch (error: any) {
      throw new Error(mapScheduleError(error));
    }
  };

  // Bloqueio de agenda (RN-118n): profissionais que o perfil pode bloquear e gravação
  const blockProfessionals = (scheduleOptions?.professionals ?? [])
    .filter(p => p.active)
    .filter(p => scheduleOptions?.writeScope !== 'own' || scheduleOptions.ownProfessionalIds.includes(p.id))
    .map(p => ({ id: p.id, name: p.name }));
  const blockDefaultProfessional = (() => {
    if (scheduleOptions?.writeScope === 'own') return scheduleOptions.ownProfessionalIds[0];
    const selected = (scheduleOptions?.professionals ?? []).filter(p => selectedProfessionals.includes(p.name));
    return selected.length === 1 ? selected[0].id : undefined;
  })();

  const handleCreateBlock = async (data: ScheduleBlockCreateData) => {
    try {
      const result = await scheduleBlocksService.create(data);
      showToast(result.ids.length > 1 ? `${result.ids.length} horários bloqueados.` : 'Horário bloqueado.', 'success');
      loadSchedules();
    } catch (error: any) {
      throw new Error(mapScheduleBlockError(error));
    }
  };

  const handleUpdateBlock = async (id: string, data: ScheduleBlockUpdateData) => {
    try {
      const updated = await scheduleBlocksService.update(id, data);
      showToast(updated > 1 ? `${updated} bloqueios atualizados.` : 'Bloqueio atualizado.', 'success');
      loadSchedules();
    } catch (error: any) {
      throw new Error(mapScheduleBlockError(error));
    }
  };

  const handleDeleteBlock = async (id: string, includeFollowing: boolean) => {
    try {
      const deleted = await scheduleBlocksService.remove(id, includeFollowing);
      showToast(deleted > 1 ? `${deleted} bloqueios excluídos.` : 'Bloqueio excluído.', 'success');
      loadSchedules();
    } catch (error: any) {
      throw new Error(mapScheduleBlockError(error));
    }
  };

  // Funções do modal de agenda do paciente
  const openPatientScheduleModal = () => {
    if (selectedPatients.length === 0) return;

    // Match por id (não por nome): evita casar "Ana Costa" com "Mariana Costa"
    const selectedPatientId = patientLabelIds.current[selectedPatients[0]];
    const patientEvents = selectedPatientId
      ? events.filter((event: ScheduleEvent) => event.patientId === selectedPatientId)
      : [];

    setPatientScheduleEvents(patientEvents);
    setIsPatientScheduleModalOpen(true);
  };

  const closePatientScheduleModal = () => {
    setIsPatientScheduleModalOpen(false);
    setPatientScheduleEvents([]);
  };

  const calculateEndTime = (startTime: string) => {
    const [hours, minutes] = startTime.split(':').map(Number);
    const endHours = hours + 1; // Novo agendamento: 1h de duração como padrão
    return `${endHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  // Fim = início + duração real (min) do agendamento — para exibir a janela verdadeira
  const endTimeFromDuration = (startTime: string, durationMin: number) => {
    const [hours, minutes] = startTime.split(':').map(Number);
    const total = hours * 60 + minutes + (durationMin || 0);
    const eh = Math.floor(total / 60) % 24;
    const em = total % 60;
    return `${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}`;
  };


  // Consulta real da agenda: o período consultado é a janela visível do calendário
  // (mês = grade de 6 semanas; semana; dia) — a navegação sempre encontra dados.
  const loadSchedules = async () => {
    if (!scheduleOptions) return;

    // Nenhum profissional marcado = nada a exibir (o filtro é sempre a lista explícita)
    if (selectedProfessionals.length === 0) {
      setEvents([]);
      setBlocks([]);
      return;
    }

    // Guarda de sequência: navegações/cliques rápidos disparam várias buscas; só a
    // última aplica o resultado (respostas fora de ordem não sobrescrevem a atual).
    const seq = ++loadSeqRef.current;
    setIsLoading(true);
    try {
      let fromDate: Date;
      let toDate: Date;
      if (calendarView === 'month') {
        const days = getDaysInMonth();
        fromDate = days[0];
        toDate = days[41];
      } else if (calendarView === 'week') {
        const days = getWeekDays();
        fromDate = days[0];
        toDate = days[6];
      } else {
        fromDate = currentDate;
        toDate = currentDate;
      }

      // Sempre a lista explícita de ids (nunca "todos = sem filtro"): assim o conjunto
      // exibido não muda de forma inconsistente ao (des)marcar um profissional.
      const professionalIds = scheduleOptions.professionals
        .filter(p => selectedProfessionals.includes(p.name))
        .map(p => p.id);
      const patientIds = selectedPatients
        .map(label => patientLabelIds.current[label])
        .filter(Boolean);

      // Bloqueio é melhor esforço: falha na leitura dele não derruba a agenda
      const [result, blockItems] = await Promise.all([
        schedulesService.getSchedules({
          from: toLocalDateStr(fromDate),
          to: toLocalDateStr(toDate),
          professionals: professionalIds.length > 0 ? professionalIds.join(',') : undefined,
          patients: patientIds.length > 0 ? patientIds.join(',') : undefined,
        }),
        scheduleBlocksService.getBlocks(toLocalDateStr(fromDate), toLocalDateStr(toDate), professionalIds.join(','))
          .catch(() => [] as ScheduleBlockItem[]),
      ]);

      if (seq !== loadSeqRef.current) return; // resposta obsoleta

      let mapped = result.items.map(i => mapScheduleItem(i, statusKinds));
      // Equipe: filtro aplicado no cliente (o volume da janela é pequeno)
      if (filters.team) {
        mapped = mapped.filter(e => e.teamName === filters.team);
      }
      setEvents(mapped);
      setBlocks(blockItems);
    } catch (error) {
      console.error('Erro ao buscar dados da agenda:', error);
    } finally {
      if (seq === loadSeqRef.current) setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSchedules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleOptions, currentDate, calendarView, selectedProfessionals, selectedPatients, filters.team]);

  // Handler para mudança nos filtros
  const handleFilterChange = (field: string, value: any) => {
    const newFilters = { ...filters, [field]: value };

    // Atualizar campos específicos baseados no tipo de filtro
    if (field === 'professionals') {
      setSelectedProfessionals(value);
    } else if (field === 'patients') {
      setSelectedPatients(value);
    } else if (field === 'startDate' && value) {
      // O período consultado segue a janela do calendário: a data inicial navega até ela
      setCurrentDate(new Date(`${value}T00:00:00`));
    } else if (field === 'team') {
      // Trocar de equipe RE-SELECIONA os profissionais dela (interseção com a seleção
      // anterior esvaziava o filtro ao alternar equipes); perfil own mantém só o próprio.
      // Pacientes: se a equipe tem lista atrelada, seleção fora dela é podada.
      const newTeam = value
        ? (scheduleOptions?.teams ?? []).find(t => t.name === value) ?? null
        : null;
      const members = (scheduleOptions?.professionals ?? [])
        .filter(p => (p.active ?? true) || showInactiveProfessionals)
        .filter(p => !newTeam || (p.teamIds ?? []).includes(newTeam.id));
      const scoped = scheduleOptions?.dataScope === 'own'
        ? members.filter(p => scheduleOptions.ownProfessionalIds.includes(p.id))
        : members;
      setSelectedProfessionals(scoped.map(p => p.name));
      // Paciente selecionado também reseta — a busca recomeça no recorte da nova equipe
      setSelectedPatients([]);
      setPatientSearchTerm('');
    }

    setFilters(newFilters);
  };

  // useEffect para pré-preencher paciente da URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const patientParam = urlParams.get('patient');

    if (patientParam) {
      // Decodificar o nome do paciente e pré-preencher o campo de busca
      const decodedPatientName = decodeURIComponent(patientParam);
      setPatientSearchTerm(decodedPatientName);
    }

    // "Ver na agenda" dos Assistentes (RN-567): abre já no dia da sessão
    const dateParam = urlParams.get('date');
    if (dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam)) {
      setCurrentDate(new Date(`${dateParam}T00:00:00`));
      setFilters(prev => ({ ...prev, startDate: dateParam }));
    }

    // ...e com o paciente já SELECIONADO, pelo id: o nome do cartão vem protegido e não serve para a busca
    const patientIdParam = urlParams.get('patientId');
    if (patientIdParam) {
      patientsService.getById(patientIdParam)
        .then((p) => {
          const label = `${p.patientName} - CPF: ${p.patientDocument || 'não informado'} - Resp: ${p.responsibleName1 || 'não informado'}`;
          patientLabelIds.current[label] = p.id;
          setSelectedPatients([label]);
          setPatientSearchTerm(p.patientName);
          setFilters(prev => ({ ...prev, patients: [label] }));
        })
        .catch(() => undefined);
    }
  }, []);


  // Função para limpar todos os filtros (o useEffect de loadSchedules refaz a consulta)
  /** A janela que a tela mostra: dia, semana ou mes — a mesma que alimenta a grade. */
  const currentWindow = (): { from: string; to: string } => {
    if (calendarView === 'month') {
      const days = getDaysInMonth();
      return { from: toLocalDateStr(days[0]), to: toLocalDateStr(days[41]) };
    }
    if (calendarView === 'week') {
      const days = getWeekDays();
      return { from: toLocalDateStr(days[0]), to: toLocalDateStr(days[6]) };
    }
    return { from: toLocalDateStr(currentDate), to: toLocalDateStr(currentDate) };
  };

  // Ancora do menu de formato da impressao (RN-120e).
  const [printMenuAnchor, setPrintMenuAnchor] = useState<HTMLElement | null>(null);

  const filteredProfessionalIds = (): string[] => (scheduleOptions?.professionals ?? [])
    .filter(p => selectedProfessionals.includes(p.name))
    .map(p => p.id);

  /**
   * Impressao em nova aba, levando o recorte da tela; la da para apertar antes de imprimir.
   * Dois formatos (RN-120e): uma folha por profissional, para entregar a cada um, ou corrido,
   * para a coordenacao ler tudo junto sem gastar uma folha por pessoa.
   */
  const handlePrintSchedule = (layout: 'por-profissional' | 'corrido') => {
    setPrintMenuAnchor(null);
    const { from, to } = currentWindow();
    const params = new URLSearchParams({
      page: 'schedule-print',
      from,
      to,
      professionals: filteredProfessionalIds().join(','),
      layout,
    });
    if (filters.team) params.set('team', filters.team);
    window.open(`${window.location.pathname}?${params.toString()}`, '_blank');
  };

  /** Planilha do mesmo recorte. O filtro de equipe e do cliente, entao vai no nome do arquivo
   *  apenas como aviso: a API nao o conhece e a planilha traria os demais. */
  const handleExportSchedule = async () => {
    setIsExporting(true);
    try {
      const { from, to } = currentWindow();
      const blob = await schedulesService.exportXlsx({
        from,
        to,
        professionals: filteredProfessionalIds().join(',') || undefined,
        patients: selectedPatients
          .map(label => patientLabelIds.current[label])
          .filter(Boolean)
          .join(',') || undefined,
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `agenda_${from}_a_${to}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      showToast('Nao foi possivel exportar a planilha. Tente novamente.', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const handleClearFilters = () => {
    const initialFilters = getInitialFilters();

    setFilters(initialFilters);
    setSelectedProfessionals(initialFilters.professionals);
    setSelectedPatients(initialFilters.patients);
    setPatientSearchTerm('');
    setHasFilterChanges(false);

    // Fechar dropdowns
    setIsPatientDropdownOpen(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return '#007bff'; // Azul - Confirmado
      case 'cancelled': return '#dc3545'; // Vermelho - Cancelado
      case 'pending': return '#868e96'; // Cinza claro - Pendente confirmação (mais claro que o "Faltou")
      case 'attended': return '#28a745'; // Verde - Compareceu
      case 'no_show': return '#6c757d'; // Cinza - Faltou sem aviso
      default: return '#6c757d';
    }
  };

  // Cor do card vem do CATÁLOGO de status (option set do Bubble: confirmação + presença,
  // com cor definida no sistema). Sem catálogo carregado ou status fora dele, cai na cor
  // canônica da tela. Data-driven: novos status/cores entram pela tabela, sem tocar no código.
  const statusColorOf = (event: ScheduleEvent): string => {
    const name = event.statusDisplay;
    if (name && scheduleOptions) {
      // ?? [] tolera backend antigo (sem os catálogos) até o deploy do endpoint novo
      const catalog = [...(scheduleOptions.confirmationStatuses ?? []), ...(scheduleOptions.presenceStatuses ?? [])];
      const match = catalog.find(s => s.name === name);
      if (match?.color) return match.color;
    }
    return getStatusColor(event.status);
  };

  // O status como a clínica o chama; o texto fixo só quando o agendamento não traz o nome
  const statusLabelOf = (event: ScheduleEvent) => event.statusDisplay || getStatusText(event.status);

  const getStatusText = (status: string) => {
    switch (status) {
      case 'confirmed': return 'Confirmado';
      case 'cancelled': return 'Cancelado';
      case 'pending': return 'Pendente Confirmação';
      case 'attended': return 'Compareceu';
      case 'no_show': return 'Faltou sem aviso';
      default: return 'Desconhecido';
    }
  };

  // Evento pertence ao profissional pelo id; sem id (legado), pelo nome — mesma regra nas colunas e no filtro
  const ofPro = (pro: { id: string; name: string }) => (e: ScheduleEvent) =>
    (e.professionalId ? e.professionalId === pro.id : e.doctor === pro.name);

  // Período da visão por profissional (dia ou semana) e quem tem agendamento nele
  const proPeriodDays = () => (calendarView === 'week' ? getWeekDays() : [currentDate]);
  const hasAgendaInPeriod = (pro: { id: string; name: string }) =>
    proPeriodDays().some(d => getEventsForDate(d).some(ofPro(pro)));

  const getEventsForDate = (date: Date) => {
    // toLocalDateStr, não toISOString: em UTC-3 a noite "pula" para o dia seguinte
    const dateStr = toLocalDateStr(date);
    return events.filter(event => event.date === dateStr);
  };

  // Bloqueios do dia (RN-118n): datas já vêm no relógio da entidade
  const getBlocksForDate = (date: Date) => {
    const dateStr = toLocalDateStr(date);
    return blocks.filter(b => b.dateTimeInitial.slice(0, 10) === dateStr);
  };
  const blockMinutes = (b: ScheduleBlockItem) => ({
    start: timeToMinutesOf(b.dateTimeInitial.slice(11, 16)),
    end: timeToMinutesOf(b.dateTimeFinal.slice(11, 16)),
  });
  const blockOwnerName = (b: ScheduleBlockItem) =>
    scheduleOptions?.professionals.find(p => p.id === b.professionalRegistrationId)?.name.split(' ').slice(0, 2).join(' ');
  // Com mais de um profissional na tela, o bloqueio diz de quem é
  const blockLabel = (b: ScheduleBlockItem) => {
    const owner = selectedProfessionals.length > 1 ? blockOwnerName(b) : undefined;
    return owner ? `${b.title} · ${owner}` : b.title;
  };
  const blockTooltip = (b: ScheduleBlockItem) =>
    `Bloqueio: ${blockLabel(b)} — ${b.dateTimeInitial.slice(11, 16)} às ${b.dateTimeFinal.slice(11, 16)}`
    + (b.notes ? `\n${b.notes}` : '') + '\nNão impede agendar neste horário.';
  const openBlockModal = (block: ScheduleBlockItem | null, date?: Date, startTime?: string) =>
    setBlockModal({ open: true, block, date: date ? toLocalDateStr(date) : undefined, startTime });
  // Bloqueios do período visível (o carregamento já busca só o período da visão)
  const periodBlockCount = blocks.length;

  // Bloqueio como item do dia: mesmo formato do agendamento para o cálculo de colunas
  const blockAsDayItem = (b: ScheduleBlockItem): DayItem => {
    const { start, end } = blockMinutes(b);
    return {
      id: `block-${b.id}`,
      date: b.dateTimeInitial.slice(0, 10),
      time: b.dateTimeInitial.slice(11, 16),
      duration: Math.max(end - start, 15),
      block: b,
    } as DayItem;
  };

  const openBlockFromKeyboard = (e: React.KeyboardEvent, b: ScheduleBlockItem) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      openBlockModal(b);
    }
  };

  // Cartão do bloqueio na visão do dia: ocupa o espaço como um agendamento, com a hachura DENTRO
  // e borda tracejada — lido de relance como "indisponível", nunca como atendimento
  const renderDayBlockCard = (event: LayoutEvent, b: ScheduleBlockItem) => (
    <div
      key={event.id}
      role="button"
      tabIndex={0}
      onClick={(e) => { e.stopPropagation(); openBlockModal(b); }}
      onKeyDown={(e) => openBlockFromKeyboard(e, b)}
      title={blockTooltip(b)}
      style={{
        position: 'absolute',
        top: event.top,
        left: event.left,
        width: event.width,
        height: event.height,
        background: `${BLOCK_HATCH}, #fdf1f2`,
        border: `1px dashed ${BLOCK_RED}`,
        color: colors.textPrimary,
        borderRadius: '8px',
        fontSize: '0.85rem',
        cursor: 'pointer',
        overflow: 'hidden',
        zIndex: event.zIndex,
        transition: 'box-shadow 0.2s ease',
        pointerEvents: 'auto',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 14px rgba(227, 93, 106, 0.25)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
    >
      {/* Texto num cabeçalho de fundo LISO: sobre a hachura o nome do profissional não se lia
          (achado do Hilton, 30/09); a hachura fica só no corpo, marcando a duração */}
      <div style={{ background: '#fdf1f2', padding: '6px 10px 7px', borderBottom: `1px dashed ${BLOCK_RED}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 700 }}>
        <EventBusy style={{ fontSize: '1rem', color: BLOCK_RED, flexShrink: 0 }} />
        <span style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {b.dateTimeInitial.slice(11, 16)}–{b.dateTimeFinal.slice(11, 16)}
        </span>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.title}</span>
      </div>
      <div style={{ fontSize: '0.8rem', fontWeight: 500, color: colors.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: '2px' }}>
        {[blockOwnerName(b), 'bloqueio de agenda'].filter(Boolean).join(' · ')}
      </div>
      </div>
    </div>
  );

  // Pílula do bloqueio na semana: mesmo tamanho da pílula de agendamento, visual de bloqueio
  const renderBlockPill = (b: ScheduleBlockItem) => (
    <div
      key={`block-${b.id}`}
      role="button"
      tabIndex={0}
      onClick={(e) => { e.stopPropagation(); openBlockModal(b); }}
      onKeyDown={(e) => openBlockFromKeyboard(e, b)}
      title={blockTooltip(b)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        // Fundo liso: na pílula o texto ocupa tudo, e sobre a hachura ele não se lia
        background: '#fdf1f2',
        border: `1px dashed ${BLOCK_RED}`,
        color: colors.textPrimary,
        padding: '1px 4px',
        borderRadius: '3px',
        fontSize: '0.85rem',
        lineHeight: '1.3',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        cursor: 'pointer',
      }}
    >
      <EventBusy style={{ fontSize: '0.9rem', color: BLOCK_RED, flexShrink: 0 }} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {`${b.dateTimeInitial.slice(11, 16)}–${b.dateTimeFinal.slice(11, 16)} ${blockLabel(b)}`}
      </span>
    </div>
  );

  const renderBlockChip = (b: ScheduleBlockItem, compact = false) => (
    <div
      key={`block-${b.id}`}
      role="button"
      tabIndex={0}
      onClick={(e) => { e.stopPropagation(); openBlockModal(b); }}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); openBlockModal(b); } }}
      title={blockTooltip(b)}
      className="schedule-block-chip"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        padding: compact ? '1px 4px' : '2px 6px',
        borderRadius: '3px',
        border: `1px dashed ${BLOCK_RED}`,
        background: '#fdf1f2',
        color: colors.textPrimary,
        fontSize: compact ? '0.78rem' : '0.82rem',
        lineHeight: 1.3,
        cursor: 'pointer',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        pointerEvents: 'auto',
      }}
    >
      <EventBusy style={{ fontSize: '0.9rem', flexShrink: 0, color: BLOCK_RED }} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {compact ? `${b.dateTimeInitial.slice(11, 16)}–${b.dateTimeFinal.slice(11, 16)} ${blockLabel(b)}` : blockLabel(b)}
      </span>
    </div>
  );

  // Card compacto reutilizado nos modais de lista (do "+" e da agenda do paciente):
  // 2 linhas — hora/faixa + nome + status; serviço · profissional. Clique abre a edição.
  const renderEventCard = (event: ScheduleEvent, key: number, dateLabel?: string) => (
    <div
      key={key}
      onClick={() => openEditModal(event)}
      style={{
        border: '1px solid #e9ecef',
        // borda esquerda colorida = status (depois do `border` para não ser sobrescrita)
        borderLeft: `5px solid ${statusColorOf(event)}`,
        borderRadius: '6px',
        padding: '0.5rem 0.75rem',
        marginBottom: '0.5rem',
        cursor: 'pointer',
        transition: 'background 0.15s',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = '#f8f9fa')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'white')}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
        <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#495057', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {event.time}{dateLabel ? ` · ${dateLabel}` : ''} — {patientDisplayName(event.patient)}
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          <PackageMark event={event} light={false} />
          <span style={{ background: statusColorOf(event), color: 'white', padding: '2px 8px', borderRadius: '10px', fontSize: '0.68rem', fontWeight: 500, whiteSpace: 'nowrap', flexShrink: 0 }}>
            {statusLabelOf(event)}
          </span>
        </span>
      </div>
      <div style={{ fontSize: '0.78rem', color: '#6c757d', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {[event.service, event.doctor].filter(Boolean).join(' · ')}
      </div>
    </div>
  );

  // Funções auxiliares para a grade de horários
  const generateTimeSlots = () => {
    const slots = [];
    // 06:00 a 23:30: a grade termina à meia-noite, senão o agendamento das 23:30 começa na borda e some
    for (let hour = 6; hour <= 23; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
      slots.push(`${hour.toString().padStart(2, '0')}:30`);
    }
    return slots;
  };

  const timeToMinutes = (time: string) => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const getEventPositionPixels = (event: ScheduleEvent) => {
    const startMinutes = timeToMinutes(event.time);
    const startTime = 6 * 60; // 06:00, primeira hora da grade
    const minutesFromStart = startMinutes - startTime;

    // Cada slot de 30 minutos = 45px
    const pixelsPerMinute = 45 / 30;
    const top = minutesFromStart * pixelsPerMinute;
    // -8px de altura cria respiro vertical entre cards consecutivos (mín. 34px legível)
    const height = Math.max(event.duration * pixelsPerMinute - 8, 34);

    return { top: `${top}px`, height: `${height}px` };
  };

  const getOverlappingEvents = (events: ScheduleEvent[], targetEvent: ScheduleEvent) => {
    const targetStart = timeToMinutes(targetEvent.time);
    const targetEnd = targetStart + targetEvent.duration;

    return events.filter(event => {
      if (event === targetEvent) return false;
      const eventStart = timeToMinutes(event.time);
      const eventEnd = eventStart + event.duration;

      return (eventStart < targetEnd && eventEnd > targetStart);
    });
  };

  const calculateEventLayoutPixels = (events: ScheduleEvent[]) => {
    // Primeiro, vamos criar grupos de eventos que realmente se sobrepõem no mesmo tempo
    const processed = new Set<number>();
    const result: LayoutEvent[] = [];

    for (let i = 0; i < events.length; i++) {
      if (processed.has(i)) continue;

      const currentEvent = events[i];
      const currentStart = timeToMinutes(currentEvent.time);
      const currentEnd = currentStart + currentEvent.duration;

      // Encontrar todos os eventos que se sobrepõem com este
      const overlappingGroup = [currentEvent];
      const overlappingIndices = [i];

      for (let j = 0; j < events.length; j++) {
        if (i === j || processed.has(j)) continue;

        const otherEvent = events[j];
        const otherStart = timeToMinutes(otherEvent.time);
        const otherEnd = otherStart + otherEvent.duration;

        // Verificar se há sobreposição real
        if (currentStart < otherEnd && currentEnd > otherStart) {
          overlappingGroup.push(otherEvent);
          overlappingIndices.push(j);
        }
      }

      // Marcar todos os eventos deste grupo como processados
      overlappingIndices.forEach(idx => processed.add(idx));

      // Ordenar o grupo por horário de início
      overlappingGroup.sort((a, b) => {
        const timeA = timeToMinutes(a.time);
        const timeB = timeToMinutes(b.time);
        if (timeA !== timeB) return timeA - timeB;
        return events.indexOf(a) - events.indexOf(b);
      });

      // Calcular posições para cada evento no grupo
      const groupSize = overlappingGroup.length;

      overlappingGroup.forEach((event, groupIndex) => {
        const columnWidth = 100 / groupSize;

        result.push({
          ...event,
          ...getEventPositionPixels(event),
          // Colunas justas com um gap fixo pequeno (2px) — reduz o vão horizontal
          width: `calc(${columnWidth}% - 2px)`,
          left: `${columnWidth * groupIndex}%`,
          zIndex: 10 + groupIndex
        });
      });
    }

    return result;
  };

  // Grade do dia SEMPRE completa (06-23h): o clique em qualquer horário cria agendamento
  const daySlots = generateTimeSlots();

  // Visão por profissional (pedido do Hilton, 30/09): profissional no topo, dias abaixo, horários
  // nas linhas. Cada coluna é um profissional num dia, então o bloqueio pinta o trecho inteiro da
  // coluna sem disputar espaço com os agendamentos — aqui os dois aparecem juntos.
  const renderProfessionalGrid = () => {
    const selectedPros = (scheduleOptions?.professionals ?? []).filter(p => selectedProfessionals.includes(p.name));
    if (selectedPros.length === 0) {
      return (
        <div style={{ padding: '3rem 1rem', textAlign: 'center', color: '#6c757d' }}>
          Selecione ao menos um profissional no filtro.
        </div>
      );
    }
    const pros = onlyWithAgenda ? selectedPros.filter(hasAgendaInPeriod) : selectedPros;
    if (pros.length === 0) {
      return (
        <div style={{ padding: '3rem 1rem', textAlign: 'center', color: '#6c757d' }}>
          Nenhum dos profissionais selecionados tem agendamento neste período.
        </div>
      );
    }
    const isWeek = calendarView === 'week';
    // Horário de atendimento da clínica por dia da semana. Sem cadastro nenhum, nada é marcado
    // (não dá para dizer o que é fechado); com cadastro, dia sem faixa = fechado o dia todo.
    const businessHours = scheduleOptions?.businessHours ?? [];
    const closedRanges = (weekday: number): [number, number][] => {
      if (businessHours.length === 0) return [];
      const open = businessHours
        .filter(h => h.weekday === weekday)
        .map(h => [h.startMinute, h.endMinute] as [number, number])
        .sort((a, b) => a[0] - b[0]);
      const gridStart = 6 * 60;
      const gridEnd = gridStart + daySlots.length * 30;
      const closed: [number, number][] = [];
      let cursor = gridStart;
      open.forEach(([start, end]) => {
        if (start > cursor) closed.push([cursor, Math.min(start, gridEnd)]);
        cursor = Math.max(cursor, end);
      });
      if (cursor < gridEnd) closed.push([cursor, gridEnd]);
      return closed.filter(([a, b]) => b > a);
    };
    // Domingo só entra se tiver algo na semana ou se a clínica atende nele: é raro e custaria uma
    // coluna por profissional
    const days = (isWeek ? getWeekDays() : [currentDate]).filter((d, i) =>
      !isWeek || i !== 0 || getEventsForDate(d).length > 0 || getBlocksForDate(d).length > 0
      || businessHours.some(h => h.weekday === 0));
    const colMin = isWeek ? PRO_COL_MIN_WEEK : PRO_COL_MIN_DAY;
    const todayStr = toLocalDateStr(new Date());
    const pxPerMin = SLOT_PX / 30;
    const ROW_PRO = 36;
    const ROW_DAY = 40;
    const headCell: React.CSSProperties = {
      position: 'sticky',
      top: 0,
      zIndex: 3,
      background: '#f8f9fa',
      borderBottom: '1px solid #e9ecef',
      display: 'flex',
      alignItems: 'center',
      fontSize: '0.85rem',
      color: '#495057',
      minWidth: 0,
    };

    return (
      <div
        ref={proScrollRef}
        className="c4u-pro-grid"
        style={{ overflow: 'auto', maxHeight: 'calc(100vh - 230px)', minHeight: '360px', overscrollBehavior: 'contain' }}
      >
        <div style={{
          display: 'grid',
          gridTemplateColumns: `64px repeat(${pros.length * days.length}, minmax(${colMin}px, 1fr))`,
          gridTemplateRows: isWeek ? `${ROW_PRO}px ${ROW_DAY}px auto` : `${ROW_DAY}px auto`,
        }}>
          <div style={{ ...headCell, left: 0, zIndex: 5, gridRow: isWeek ? 'span 2' : undefined, borderRight: '1px solid #e9ecef' }} />

          {/* Profissional no topo; o nome acompanha a rolagem dentro do grupo de dias */}
          {pros.map((pro, pi) => (
            <div
              key={pro.id}
              title={pro.name}
              style={{
                ...headCell,
                gridColumn: isWeek ? `span ${days.length}` : undefined,
                height: isWeek ? ROW_PRO : ROW_DAY,
                // Cor sim, cor não entre profissionais (pedido do Hilton, 30/09): onde termina um e
                // começa o outro se lê sem linha
                background: pi % 2 === 0 ? colors.primarySoft : '#e9ecef',
                borderRight: 'none',
                fontWeight: 700,
                fontSize: '0.9rem',
                color: '#1f2d3d',
                justifyContent: isWeek ? 'flex-start' : 'center',
              }}
            >
              <span style={{
                position: isWeek ? 'sticky' : 'static',
                left: 72,
                padding: '0 0.5rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {pro.name}
              </span>
            </div>
          ))}

          {isWeek && pros.flatMap(pro => days.map((d, di) => {
            const isToday = toLocalDateStr(d) === todayStr;
            return (
              <div
                key={`h|${pro.id}|${toLocalDateStr(d)}`}
                style={{
                  ...headCell,
                  top: ROW_PRO,
                  justifyContent: 'center',
                  gap: '0.3rem',
                  background: isToday ? '#e3f2fd' : '#f8f9fa',
                  color: isToday ? '#1976d2' : '#495057',
                  borderRight: `1px solid ${di === days.length - 1 ? GRID_COL_GROUP : GRID_COL}`,
                }}
              >
                <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>{['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'][d.getDay()]}</span>
                <span style={{ fontWeight: isToday ? 700 : 500, fontVariantNumeric: 'tabular-nums' }}>{d.getDate()}</span>
              </div>
            );
          }))}

          {/* Horários fixos à esquerda durante a rolagem lateral */}
          <div style={{ position: 'sticky', left: 0, zIndex: 2, background: '#f8f9fa', borderRight: '1px solid #e9ecef' }}>
            {daySlots.map(time => (
              <div key={time} style={{
                height: SLOT_PX,
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.85rem',
                color: '#6c757d',
                fontWeight: time.endsWith(':00') ? 500 : 400,
                borderBottom: time.endsWith(':00') ? GRID_LINE_HALF : GRID_LINE_HOUR,
              }}>
                {time.endsWith(':00') ? time : ''}
              </div>
            ))}
          </div>

          {pros.flatMap(pro => days.map((d, di) => {
            const dateStr = toLocalDateStr(d);
            // Na visão do dia toda coluna é "hoje": o tom azulado pintaria a grade inteira
            const isToday = isWeek && dateStr === todayStr;
            const colEvents = calculateEventLayoutPixels(getEventsForDate(d).filter(ofPro(pro)));
            const colBlocks = getBlocksForDate(d).filter(b => b.professionalRegistrationId === pro.id);
            return (
              <div
                key={`c|${pro.id}|${dateStr}`}
                style={{
                  // Contexto próprio de empilhamento: cartões não passam por cima dos horários fixos
                  position: 'relative',
                  zIndex: 0,
                  height: daySlots.length * SLOT_PX,
                  background: isToday ? '#f5fbff' : 'white',
                  borderRight: `1px solid ${di === days.length - 1 ? GRID_COL_GROUP : GRID_COL}`,
                }}
              >
                {daySlots.map((time, ti) => {
                  const slotBg = time.endsWith(':00') ? (isToday ? '#eef6fc' : '#fafafa') : 'transparent';
                  return (
                    <div
                      key={time}
                      onClick={() => openNewAppointmentModal(d, time, pro.name)}
                      style={{
                        position: 'absolute',
                        top: ti * SLOT_PX,
                        left: 0,
                        right: 0,
                        height: SLOT_PX,
                        boxSizing: 'border-box',
                        borderBottom: time.endsWith(':00') ? GRID_LINE_HALF : GRID_LINE_HOUR,
                        background: slotBg,
                        cursor: 'pointer',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#e3f2fd'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = slotBg; }}
                    />
                  );
                })}

                {/* Fora do atendimento da clínica: cinza liso que não captura clique (agendar
                    fora do horário continua possível, como em qualquer outra visão) */}
                {closedRanges(d.getDay()).map(([start, end]) => (
                  <div
                    key={`closed-${start}`}
                    aria-hidden
                    style={{
                      position: 'absolute',
                      top: (start - 6 * 60) * pxPerMin,
                      height: (end - start) * pxPerMin,
                      left: 0,
                      right: 0,
                      background: CLOSED_FILL,
                      pointerEvents: 'none',
                    }}
                  />
                ))}

                {/* Bloqueio: o trecho inteiro da coluna; só a etiqueta captura clique, o resto
                    continua agendável (o bloqueio não impede agendar) */}
                {colBlocks.map(b => {
                  const { start, end } = blockMinutes(b);
                  return (
                    <div
                      key={b.id}
                      style={{
                        position: 'absolute',
                        top: (start - 6 * 60) * pxPerMin,
                        height: Math.max((end - start) * pxPerMin, 24),
                        left: 0,
                        right: 0,
                        zIndex: 1,
                        background: `${BLOCK_HATCH}, #fffafa`,
                        border: `1px dashed ${BLOCK_RED}`,
                        boxSizing: 'border-box',
                        pointerEvents: 'none',
                      }}
                    >
                      <div
                        role="button"
                        tabIndex={0}
                        title={blockTooltip(b)}
                        onClick={(e) => { e.stopPropagation(); openBlockModal(b); }}
                        onKeyDown={(e) => openBlockFromKeyboard(e, b)}
                        className="c4u-pro-block-label"
                        style={{
                          pointerEvents: 'auto',
                          cursor: 'pointer',
                          background: '#fdf1f2',
                          color: BLOCK_RED_STRONG,
                          borderBottom: `1px dashed ${BLOCK_RED}`,
                          padding: '2px 6px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {b.dateTimeInitial.slice(11, 16)}–{b.dateTimeFinal.slice(11, 16)}
                        </span>{' '}
                        {b.title}
                      </div>
                    </div>
                  );
                })}

                {colEvents.map(ev => (
                  <div
                    key={ev.id}
                    role="button"
                    tabIndex={0}
                    title={eventTooltip(ev)}
                    className="c4u-pro-card"
                    onClick={(e) => { e.stopPropagation(); openEditModal(ev); }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openEditModal(ev); }
                    }}
                    style={{
                      position: 'absolute',
                      top: ev.top,
                      height: ev.height,
                      left: `calc(${ev.left} + 2px)`,
                      width: `calc(${ev.width} - 2px)`,
                      zIndex: ev.zIndex,
                      boxSizing: 'border-box',
                      background: statusColorOf(ev),
                      color: 'white',
                      borderRadius: '6px',
                      padding: '3px 6px',
                      fontSize: '0.78rem',
                      lineHeight: 1.25,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.15)',
                    }}
                  >
                    <div style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', display: 'flex', alignItems: 'center', gap: 4 }}>
                      {ev.time}
                      <PackageMark event={ev} compact />
                    </div>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {patientDisplayName(ev.patient)}
                    </div>
                    {parseFloat(ev.height) >= PRO_STATUS_MIN_HEIGHT && (
                      <div style={{
                        fontSize: '0.68rem',
                        fontStyle: 'italic',
                        textAlign: 'center',
                        padding: '1px 5px',
                        marginTop: '3px',
                        background: 'rgba(255,255,255,0.2)',
                        borderRadius: '4px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                        {statusLabelOf(ev)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          }))}
        </div>
      </div>
    );
  };

  // Dia já posicionado (view|data|tem evento): impede a agenda de rolar sozinha a cada recarga
  const posicionadoRef = React.useRef<string | null>(null);
  const proScrollRef = React.useRef<HTMLDivElement>(null);

  // Modo dia abre POSICIONADO, e o alvo é o COMPROMISSO, não o relógio (pedido do Hilton,
  // 22/09/2026). A grade vai de 06:00 a 24:00 e a página é que rola, então o alvo é a linha do
  // slot; o cabeçalho fixo é descontado para ela não ficar escondida atrás dele.
  //
  // Três regras, nesta ordem:
  //   1. Duas horas ANTES do horário atual — quem chega às 14h quer ver o que acabou de passar,
  //      não começar a tela na sessão das 14h sem contexto.
  //   2. Nunca depois do ÚLTIMO compromisso do dia. Às 23h a agenda não vai para as 23h: vai
  //      para o fim do movimento real (as 19h, por exemplo), senão abre num vazio.
  //   3. Nunca antes do PRIMEIRO compromisso. De manhã cedo mostra logo a primeira sessão, em
  //      vez de uma faixa vazia das 6h.
  // Dia sem compromisso nenhum cai na regra 1 pura, limitada à grade.
  useEffect(() => {
    if (calendarView !== 'day') return;

    const GRADE_INICIO = 6 * 60;
    const GRADE_FIM = 23 * 60 + 30;

    const doDia = getEventsForDate(currentDate)
      .map(e => timeToMinutes(e.time))
      .filter(m => Number.isFinite(m));

    // Posiciona UMA vez por dia exibido. `events` está nas dependências porque a lista chega
    // depois da troca de data (busca assíncrona) e o alvo depende dela — mas sem esta trava a
    // página rolaria sozinha a cada recarga, inclusive depois de salvar um agendamento, jogando
    // o usuário para longe do que ele acabou de mexer.
    const chave = `${calendarView}|${scheduleLayout}|${toLocalDateStr(currentDate)}|${doDia.length > 0}`;
    if (posicionadoRef.current === chave) return;
    posicionadoRef.current = chave;

    const agora = new Date();
    const ehHoje = toLocalDateStr(agora) === toLocalDateStr(currentDate);
    // Em outro dia não há "agora": mostra desde o primeiro compromisso.
    let alvoMin = ehHoje
      ? agora.getHours() * 60 + agora.getMinutes() - 120
      : (doDia.length ? Math.min(...doDia) : GRADE_INICIO);

    if (doDia.length) {
      alvoMin = Math.min(alvoMin, Math.max(...doDia));
      alvoMin = Math.max(alvoMin, Math.min(...doDia));
    }
    alvoMin = Math.min(Math.max(alvoMin, GRADE_INICIO), GRADE_FIM);

    // Arredonda PARA BAIXO no slot de 30 min: o compromisso-alvo fica visível, não cortado em cima
    const slot = Math.floor(alvoMin / 30) * 30;
    const alvo = `${Math.floor(slot / 60).toString().padStart(2, '0')}:${(slot % 60).toString().padStart(2, '0')}`;

    // Depois do render da grade, senão o elemento ainda não existe
    const frame = requestAnimationFrame(() => {
      if (proLayout) {
        proScrollRef.current?.scrollTo({ top: Math.max(0, (slot - 6 * 60) * (SLOT_PX / 30)), behavior: 'auto' });
        return;
      }
      const linha = document.getElementById(`day-slot-${alvo}`);
      if (!linha) return;
      const header = document.querySelector<HTMLElement>('.login-header');
      const deslocamento = (header?.getBoundingClientRect().height ?? 85) + 12;
      const topo = linha.getBoundingClientRect().top + window.scrollY - deslocamento;
      window.scrollTo({ top: Math.max(0, topo), behavior: 'auto' });
    });

    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarView, scheduleLayout, currentDate, events]);

  // Menu items dinâmicos baseados no perfil do usuário com navegação funcional
  const loggedMenuItems = userSession?.menuItems?.map(item => ({
    ...item,
    onClick: (e: React.MouseEvent) => {
      e.preventDefault();
      const clinic = new URLSearchParams(window.location.search).get('clinic') || 'ninho';

      switch (item.label) {
        case 'Dashboard':
          window.location.href = `${window.location.origin}/?page=dashboard&clinic=${clinic}`;
          break;
        case 'Agenda':
          window.location.href = `${window.location.origin}/?page=schedule&clinic=${clinic}`;
          break;
        case 'Pacientes':
          alert('Página de Pacientes em desenvolvimento');
          break;
        case 'Relatórios':
          alert('Página de Relatórios em desenvolvimento');
          break;
        case 'Financeiro':
          alert('Página Financeiro em desenvolvimento');
          break;
        case 'Usuários':
          alert('Página de Usuários em desenvolvimento');
          break;
        case 'Configurações':
          alert('Página de Configurações em desenvolvimento');
          break;
        default:
          console.log('Menu item clicked:', item.label);
      }
    }
  })) || [];

  if (!userSession) {
    return (
      <div className="login-page">
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          color: '#03B4C6',
          fontSize: '1.2rem'
        }}>
          Carregando...
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      {/* CSS para animação de loading */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={userSession.email}
        userProfile={userSession.role}
        clinicName={userSession.clinicName}
        notificationCount={27}
        onRevalidateLogin={handleRevalidateLogin}
        onNotificationClick={handleNotificationClick}
        onUserClick={handleUserClick}
        onLogoClick={handleLogoClick}
      />

      <Box className="c4u-main">
        <Container className={`c4u-page schedule-page${topCollapsed ? " schedule-page--collapsed" : ""}`} maxWidth={false} disableGutters>
          <Box>
            {/* Título da Página */}
            <div className="c4u-page-header c4u-page-header--in-panel">
              <div className="c4u-page-header__texts">
                <h1 className="c4u-page-header__title">Agenda Profissional</h1>
                <p className="c4u-page-header__subtitle">
                  Gerencie sua agenda, visualize compromissos e organize horários de atendimento.
                </p>
              </div>
              <div className="c4u-page-header__actions">
                <FaqButton />
              </div>
            </div>

            {/* Filtros da Agenda */}
            <Paper className="c4u-panel" elevation={0}>
            {/* Item 132: filtros + navegação presos abaixo do cabeçalho enquanto a grade rola */}
            <div className="schedule-sticky-top">
              <div className="c4u-filters__row">
                <div className="c4u-filters__fields">
                {/* Ir para data: navega o calendário até o dia/semana/mês escolhido */}
                <Box>
                  <TextField
                    label="Ir para data"
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '40px',
                        fontSize: '1rem',
                        backgroundColor: 'white',
                        '& fieldset': {
                          borderColor: '#ced4da',
                          legend: {
                            maxWidth: '100%',
                          },
                        },
                        '&:hover fieldset': {
                          borderColor: '#ced4da',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#03B4C6',
                          boxShadow: '0 0 0 3px rgba(3, 180, 198, 0.1)',
                        },
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: '0.375rem 0.5rem',
                        color: '#495057',
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '0.95rem',
                        color: '#6c757d',
                        backgroundColor: 'white',
                        paddingLeft: '4px',
                        paddingRight: '4px',
                        '&.Mui-focused': {
                          color: '#03B4C6',
                        },
                      },
                    }}
                  />
                </Box>

                {/* Equipe ANTES do profissional (RN-116): a equipe recorta o dropdown de
                    profissionais (só membros) e, com pacientes atrelados, as buscas de paciente */}
                <Box>
                  <TextField
                    select
                    label="Equipe"
                    value={filters.team}
                    onChange={(e) => handleFilterChange('team', e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    SelectProps={{
                      displayEmpty: true,
                      MenuProps: {
                        variant: 'menu',
                        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                        transformOrigin: { vertical: 'top', horizontal: 'left' },
                      },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '40px',
                        fontSize: '1rem',
                        backgroundColor: 'white',
                        '& fieldset': {
                          borderColor: '#ced4da',
                          legend: {
                            maxWidth: '100%',
                          },
                        },
                        '&:hover fieldset': {
                          borderColor: '#ced4da',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#03B4C6',
                          boxShadow: '0 0 0 3px rgba(3, 180, 198, 0.1)',
                        },
                      },
                      '& .MuiSelect-select': {
                        padding: '0.375rem 0.5rem',
                        color: '#495057',
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '0.95rem',
                        color: '#6c757d',
                        backgroundColor: 'white',
                        paddingLeft: '4px',
                        paddingRight: '4px',
                        '&.Mui-focused': {
                          color: '#03B4C6',
                        },
                      },
                    }}
                  >
                    <MenuItem value="">Todas as equipes</MenuItem>
                    {(scheduleOptions?.teams ?? []).map(team => (
                      <MenuItem key={team.id} value={team.name}>{team.name}</MenuItem>
                    ))}
                  </TextField>
                </Box>

                {/* Nome do Profissional */}
                <Box sx={{ minWidth: 0 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel
                      id="professional-select-label"
                      shrink
                      sx={{
                        fontSize: '0.95rem',
                        color: '#6c757d',
                        backgroundColor: 'white',
                        paddingLeft: '4px',
                        paddingRight: '4px',
                        '&.Mui-focused': {
                          color: '#03B4C6',
                        },
                      }}
                    >
                      Nome do Profissional
                    </InputLabel>
                    <Select
                      labelId="professional-select-label"
                      multiple
                      disabled={isOwnDataScope}
                      value={selectedProfessionals}
                      onChange={(e) => {
                        const value = e.target.value;
                        const filteredValue = (typeof value === 'string' ? value.split(',') : value)
                          .filter((v: string) => v !== 'all' && v !== '__inactive');
                        setSelectedProfessionals(filteredValue);
                      }}
                      input={<OutlinedInput label="Nome do Profissional" notched />}
                      renderValue={(selected) => {
                        if (selected.length === 0) {
                          return <span style={{ color: '#6c757d' }}>Selecione</span>;
                        }
                        if (selected.length === 1) {
                          return selected[0].split(' - ')[0];
                        }
                        return `${selected.length} selecionados`;
                      }}
                      MenuProps={{
                        // Abre abaixo do campo, alinhado à borda esquerda (sem a variante
                        // "selectedMenu", que centraliza o menu sobre o item selecionado)
                        variant: 'menu',
                        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                        transformOrigin: { vertical: 'top', horizontal: 'left' },
                        PaperProps: {
                          style: {
                            maxHeight: 300,
                          },
                        },
                      }}
                      sx={{
                        height: '40px',
                        fontSize: '1rem',
                        backgroundColor: 'white',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#ced4da',
                          legend: {
                            maxWidth: '100%',
                          },
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#ced4da',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#03B4C6',
                          boxShadow: '0 0 0 3px rgba(3, 180, 198, 0.1)',
                        },
                        '& .MuiSelect-select': {
                          padding: '0.375rem 0.5rem',
                          color: '#495057',
                        },
                      }}
                    >
                      <MenuItem
                        value="all"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSelectAllProfessionals();
                        }}
                        sx={{
                          backgroundColor: '#f8f9fa !important',
                          fontWeight: 600,
                          fontSize: '0.85rem',
                          borderBottom: '1px solid #e9ecef',
                        }}
                      >
                        <Checkbox
                          checked={selectedProfessionals.length === professionalsList.length}
                          indeterminate={selectedProfessionals.length > 0 && selectedProfessionals.length < professionalsList.length}
                          sx={{
                            padding: '4px 8px 4px 4px',
                            '& .MuiSvgIcon-root': {
                              fontSize: '1.2rem',
                            },
                          }}
                        />
                        <ListItemText
                          primary={`Selecionar Todos (${professionalsList.length})`}
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight: 600,
                          }}
                        />
                      </MenuItem>
                      {/* Inativos sob demanda: consulta de histórico de quem saiu */}
                      <MenuItem
                        value="__inactive"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowInactiveProfessionals(prev => {
                            const next = !prev;
                            if (!next) {
                              // Ao esconder, tira os inativos da seleção
                              setSelectedProfessionals(sel => sel.filter(name =>
                                (scheduleOptions?.professionals ?? []).find(o => o.name === name)?.active ?? true));
                            }
                            return next;
                          });
                        }}
                        sx={{
                          backgroundColor: '#f8f9fa !important',
                          fontSize: '0.85rem',
                          borderBottom: '1px solid #e9ecef',
                        }}
                      >
                        <Checkbox
                          checked={showInactiveProfessionals}
                          sx={{
                            padding: '4px 8px 4px 4px',
                            '& .MuiSvgIcon-root': {
                              fontSize: '1.2rem',
                            },
                          }}
                        />
                        <ListItemText
                          primary="Mostrar inativos"
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight: 500,
                          }}
                        />
                      </MenuItem>
                      {professionalsList.map((professional) => (
                        <MenuItem
                          key={professional}
                          value={professional}
                          sx={{
                            fontSize: '0.85rem',
                            '&:hover': {
                              backgroundColor: '#f8f9fa',
                            },
                          }}
                        >
                          <Checkbox
                            checked={selectedProfessionals.includes(professional)}
                            sx={{
                              padding: '4px 8px 4px 4px',
                              '& .MuiSvgIcon-root': {
                                fontSize: '1.2rem',
                              },
                            }}
                          />
                          <ListItemText
                            primary={professional.split(' - ')[0]}
                            secondary={visibleProfessionals.find(o => o.name === professional)?.active === false
                              ? 'Inativo'
                              : professional.split(' - ')[1]}
                            primaryTypographyProps={{
                              fontSize: '0.85rem',
                              fontWeight: 500,
                              color: '#495057',
                            }}
                            secondaryTypographyProps={{
                              fontSize: '0.75rem',
                              color: '#6c757d',
                            }}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Busca paciente */}
                <div className="schedule-patient-search" style={{ position: 'relative', minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <div style={{ flex: 1, position: 'relative' }}>
                      {/* Campo de busca */}
                      <TextField
                        label="Busca paciente cadastrado"
                        placeholder="Digite o nome do paciente..."
                        value={selectedPatients.length > 0 ? selectedPatients[0].split(' - ')[0] : patientSearchTerm}
                        onChange={handlePatientSearchChange}
                        onClick={(e) => e.stopPropagation()}
                        fullWidth
                        size="small"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        InputProps={{
                          endAdornment: (selectedPatients.length > 0 || patientSearchTerm) ? (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                aria-label="Limpar paciente"
                                onClick={(e) => { e.stopPropagation(); clearPatientSelection(); }}
                                sx={{ padding: '2px' }}
                              >
                                <Close sx={{ fontSize: '1.1rem', color: '#6c757d' }} />
                              </IconButton>
                            </InputAdornment>
                          ) : undefined,
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            height: '40px',
                            fontSize: '1rem',
                            backgroundColor: 'white',
                            '& fieldset': {
                              borderColor: '#ced4da',
                              legend: {
                                maxWidth: '100%',
                              },
                            },
                            '&:hover fieldset': {
                              borderColor: '#ced4da',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#03B4C6',
                              boxShadow: '0 0 0 3px rgba(3, 180, 198, 0.1)',
                            },
                          },
                          '& .MuiOutlinedInput-input': {
                            padding: '0.375rem 0.5rem',
                            color: '#495057',
                            fontWeight: selectedPatients.length > 0 ? '500' : 'normal',
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '0.95rem',
                            color: '#6c757d',
                            backgroundColor: 'white',
                            paddingLeft: '4px',
                            paddingRight: '4px',
                            '&.Mui-focused': {
                              color: '#03B4C6',
                            },
                          },
                        }}
                      />

                      {/* Dropdown com resultados */}
                      {isPatientDropdownOpen && getFilteredPatients().length > 0 && (
                        <div style={{
                          position: 'absolute',
                          top: 'calc(100% + 2px)',
                          left: 0,
                          right: 0,
                          background: 'white',
                          border: '1px solid #ced4da',
                          borderRadius: '4px',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                          zIndex: 1000,
                          maxHeight: '250px',
                          overflowY: 'auto'
                        }}>

                          {/* Lista de pacientes filtrados */}
                          {getFilteredPatients().map((patient, index) => (
                            <div
                              key={index}
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePatientToggle(patient);
                              }}
                              style={{
                                padding: '0.75rem 0.5rem',
                                borderBottom: index < getFilteredPatients().length - 1 ? '1px solid #e9ecef' : 'none',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                transition: 'background 0.2s',
                                fontSize: '0.85rem'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                            >
                              <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <span style={{
                                  fontWeight: '500',
                                  color: '#495057'
                                }}>
                                  {patient.split(' - ')[0]}
                                </span>
                                <span style={{
                                  fontSize: '0.75rem',
                                  color: '#6c757d',
                                  marginTop: '2px'
                                }}>
                                  {patient.split(' - ').slice(1).join(' - ')}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                    </div>

                  </div>
                </div>
                </div>
              <div className="c4u-filters__actions">
                        <ClearFiltersButton
                          onClick={handleClearFilters}
                          disabled={!hasFilterChanges || isLoading}
                        />
                  <Tooltip title="Imprimir a agenda filtrada">
                    <span>
                      <IconButton
                        onClick={(e) => setPrintMenuAnchor(e.currentTarget)}
                        className="c4u-btn-icon c4u-btn-icon--add"
                      >
                        <Print />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Menu
                    anchorEl={printMenuAnchor}
                    open={Boolean(printMenuAnchor)}
                    onClose={() => setPrintMenuAnchor(null)}
                  >
                    <MenuItem onClick={() => handlePrintSchedule('por-profissional')}>
                      Um profissional por folha
                    </MenuItem>
                    <MenuItem onClick={() => handlePrintSchedule('corrido')}>
                      Sem separação (corrido)
                    </MenuItem>
                  </Menu>

                  <Tooltip title="Exportar para Excel o que esta filtrado">
                    <span>
                      <IconButton
                        onClick={handleExportSchedule}
                        disabled={isExporting || isLoading}
                        className="c4u-btn-icon c4u-btn-icon--add"
                      >
                        <TableChart />
                      </IconButton>
                    </span>
                  </Tooltip>

                  <Tooltip title={selectedPatients.length === 0 ? 'Escolha um paciente para agendar' : 'Agendar para o paciente'}>
                    <span>
                      <IconButton
                        onClick={openPatientScheduleModal}
                        disabled={selectedPatients.length === 0}
                        className="c4u-btn-icon c4u-btn-icon--add"
                      >
                        <EventNote />
                      </IconButton>
                    </span>
                  </Tooltip>
  
                  {schedulePerms.canBlockEdit && (
                  <Tooltip title="Bloquear agenda: reunião, almoço, horário fechado (não impede agendar)">
                    <IconButton
                      onClick={() => openBlockModal(null, currentDate)}
                      className="c4u-btn-icon c4u-btn-icon--block"
                      aria-label="Bloquear agenda"
                    >
                      <EventBusy />
                    </IconButton>
                  </Tooltip>
                  )}

                  <Tooltip title={selectedPatients.length === 0 ? 'Escolha um paciente para abrir o cadastro' : 'Abrir o cadastro do paciente'}>
                    <span>
                      <IconButton
                        onClick={() => {
                          // O id real (GUID) vem do mapa rótulo->id, não do prefixo do rótulo
                          const patientId = selectedPatients.length > 0
                            ? patientLabelIds.current[selectedPatients[0]]
                            : undefined;
                          if (patientId) {
                            const registerUrl = `${window.location.origin}${window.location.pathname}?page=patient-register&id=${patientId}`;
                            window.open(registerUrl, '_blank');
                          }
                        }}
                        disabled={selectedPatients.length === 0}
                        className="c4u-btn-icon c4u-btn-icon--add"
                      >
                        <Folder />
                      </IconButton>
                    </span>
                  </Tooltip>
  
                  <Tooltip title="Novo paciente">
                    <IconButton onClick={handleNewPatientClick} className="c4u-btn-icon c4u-btn-icon--add">
                      <PersonAdd />
                    </IconButton>
                  </Tooltip>
  
                  <Menu
                    anchorEl={newPatientMenuAnchor}
                    open={newPatientMenuAnchor !== null}
                    onClose={() => setNewPatientMenuAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  >
                    <MenuItem onClick={() => { setNewPatientMenuAnchor(null); setIsQuickRegisterOpen(true); }}>
                      <ListItemIcon><Bolt fontSize="small" /></ListItemIcon>
                      <ListItemText primary="Cadastro rápido" secondary="Dados mínimos, em um modal" />
                    </MenuItem>
                    <MenuItem onClick={handleFullRegisterClick}>
                      <ListItemIcon><AssignmentInd fontSize="small" /></ListItemIcon>
                      <ListItemText primary="Cadastro completo" secondary="Página com todos os dados" />
                    </MenuItem>
                  </Menu>
  
                  <Tooltip title="Abrir a lista de pacientes">
                    <IconButton
                      onClick={() => {
                        const patientsUrl = `${window.location.origin}${window.location.pathname}?page=patients`;
                        window.open(patientsUrl, '_blank');
                      }}
                      className="c4u-btn-icon c4u-btn-icon--add"
                    >
                      <List />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Agenda do dia: lista das sessões com envio de WhatsApp">
                    <IconButton onClick={goToDaySchedule} className="c4u-btn-icon c4u-btn-icon--add" aria-label="Agenda do dia">
                      <EventAvailable />
                    </IconButton>
                  </Tooltip>
              </div>
              </div>


            {/* Navegação do Calendário */}
            <div className="schedule-calendar-nav" style={{
              padding: '1rem 0'
            }}>
              <div className="schedule-calendar-controls" style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div className="schedule-calendar-nav-buttons" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <Tooltip title={topCollapsed ? 'Mostrar título e filtros' : 'Recolher título e filtros (mais espaço para a agenda)'}>
                    <IconButton
                      onClick={toggleTopCollapsed}
                      size="small"
                      aria-label={topCollapsed ? 'Mostrar título e filtros' : 'Recolher título e filtros'}
                      sx={{ color: '#495057', border: '1px solid #ced4da', borderRadius: '4px', padding: '6px' }}
                    >
                      {topCollapsed ? <UnfoldMore fontSize="small" /> : <UnfoldLess fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  <button
                    onClick={goToToday}
                    title="Ir para hoje"
                    style={{
                      padding: '0.5rem 1rem',
                      background: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '0.95rem',
                      cursor: 'pointer'
                    }}>
                    Hoje
                  </button>
                  <button
                    onClick={goToPrevious}
                    title="Período anterior"
                    style={{
                      padding: '0.5rem',
                      background: '#495057',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}>
                    ◀
                  </button>
                  <button
                    onClick={goToNext}
                    title="Próximo período"
                    style={{
                      padding: '0.5rem',
                      background: '#495057',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}>
                    ▶
                  </button>
                  {proLayout && (() => {
                    // Só quem tem agenda no período: esconde as colunas vazias (padrão: todas).
                    // Desabilitado quando não esconderia ninguém — todos os selecionados têm agenda.
                    const selected = (scheduleOptions?.professionals ?? []).filter(p => selectedProfessionals.includes(p.name));
                    const withAgenda = selected.filter(hasAgendaInPeriod).length;
                    const nothingToHide = withAgenda === selected.length;
                    const on = onlyWithAgenda && !nothingToHide;
                    return (
                      <button
                        type="button"
                        aria-pressed={on}
                        disabled={nothingToHide}
                        onClick={() => setOnlyWithAgenda(v => !v)}
                        title={nothingToHide
                          ? 'Todos os profissionais selecionados têm agendamento no período'
                          : on ? 'Mostrar todos os profissionais' : 'Mostrar só quem tem agendamento no período'}
                        // c4u-blocks-mode: no celular vira linha de largura total, como o seletor de Bloqueios
                        className="c4u-day-mode-option c4u-blocks-mode"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.5rem',
                          marginLeft: '0.5rem',
                          padding: '0.4rem 0.8rem',
                          minHeight: '2.35rem',
                          borderRadius: '6px',
                          border: `1px solid ${on ? colors.primary : '#ced4da'}`,
                          background: on ? colors.primarySoft : '#ffffff',
                          color: nothingToHide ? '#adb5bd' : colors.primaryText,
                          fontSize: '0.9rem',
                          fontWeight: on ? 600 : 500,
                          whiteSpace: 'nowrap',
                          cursor: nothingToHide ? 'not-allowed' : 'pointer',
                          transition: 'background-color 160ms ease-out, border-color 160ms ease-out',
                        }}
                      >
                        <span aria-hidden style={{
                          position: 'relative',
                          width: '30px',
                          height: '18px',
                          borderRadius: '999px',
                          background: on ? colors.primaryText : '#ced4da',
                          transition: 'background-color 180ms ease-out',
                          flexShrink: 0,
                        }}>
                          <span style={{
                            position: 'absolute',
                            top: '2px',
                            left: on ? '14px' : '2px',
                            width: '14px',
                            height: '14px',
                            borderRadius: '50%',
                            background: '#ffffff',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.25)',
                            transition: 'left 180ms cubic-bezier(0.22, 1, 0.36, 1)',
                          }} />
                        </span>
                        Com agenda
                        <span
                          aria-label={`${withAgenda} de ${selected.length} profissionais com agendamento no período`}
                          style={{
                            minWidth: '1.35rem',
                            padding: '0 0.35rem',
                            borderRadius: '999px',
                            fontSize: '0.78rem',
                            lineHeight: '1.35rem',
                            fontWeight: 700,
                            fontVariantNumeric: 'tabular-nums',
                            background: nothingToHide ? '#dee2e6' : colors.primaryText,
                            color: nothingToHide ? '#868e96' : '#ffffff',
                          }}
                        >
                          {withAgenda}
                        </span>
                      </button>
                    );
                  })()}
                  {!proLayout && (() => {
                    // Liga/desliga a camada de bloqueios; desabilitado sem bloqueio no período
                    const none = periodBlockCount === 0;
                    const on = showBlocks && !none;
                    return (
                      <button
                        type="button"
                        aria-pressed={on}
                        disabled={none}
                        onClick={() => setShowBlocks(v => !v)}
                        title={none
                          ? 'Nenhum bloqueio de agenda no período'
                          : on ? 'Ocultar os bloqueios de agenda' : 'Mostrar os bloqueios de agenda'}
                        className="c4u-day-mode-option c4u-blocks-mode"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.5rem',
                          marginLeft: '0.5rem',
                          padding: '0.4rem 0.8rem',
                          minHeight: '2.35rem',
                          borderRadius: '6px',
                          border: `1px solid ${on ? BLOCK_RED : '#ced4da'}`,
                          background: on ? '#fdf1f2' : '#ffffff',
                          color: none ? '#adb5bd' : BLOCK_RED_STRONG,
                          fontSize: '0.9rem',
                          fontWeight: on ? 600 : 500,
                          whiteSpace: 'nowrap',
                          cursor: none ? 'not-allowed' : 'pointer',
                          transition: 'background-color 160ms ease-out, border-color 160ms ease-out',
                        }}
                      >
                        {/* Chave liga/desliga */}
                        <span aria-hidden style={{
                          position: 'relative',
                          width: '30px',
                          height: '18px',
                          borderRadius: '999px',
                          background: on ? BLOCK_RED_STRONG : '#ced4da',
                          transition: 'background-color 180ms ease-out',
                          flexShrink: 0,
                        }}>
                          <span style={{
                            position: 'absolute',
                            top: '2px',
                            left: on ? '14px' : '2px',
                            width: '14px',
                            height: '14px',
                            borderRadius: '50%',
                            background: '#ffffff',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.25)',
                            transition: 'left 180ms cubic-bezier(0.22, 1, 0.36, 1)',
                          }} />
                        </span>
                        Bloqueios
                        <span
                          aria-label={`${periodBlockCount} ${periodBlockCount === 1 ? 'bloqueio' : 'bloqueios'}`}
                          style={{
                            minWidth: '1.35rem',
                            padding: '0 0.35rem',
                            borderRadius: '999px',
                            fontSize: '0.78rem',
                            lineHeight: '1.35rem',
                            fontWeight: 700,
                            fontVariantNumeric: 'tabular-nums',
                            background: none ? '#dee2e6' : BLOCK_RED_STRONG,
                            color: none ? '#868e96' : '#ffffff',
                          }}
                        >
                          {periodBlockCount}
                        </span>
                      </button>
                    );
                  })()}
                </div>

                <h2 style={{
                  margin: '0',
                  fontSize: '1.2rem',
                  fontWeight: '600',
                  color: '#495057'
                }}>
                  {getCalendarTitle()}
                </h2>

                <div className="schedule-calendar-view-buttons" style={{ display: 'flex', gap: '0.5rem' }}>
                  {scheduleLayout !== 'professional' && (
                  <button
                    onClick={() => setCalendarView('month')}
                    title="Visualizar mês"
                    style={{
                      padding: '0.5rem 1rem',
                      background: calendarView === 'month' ? '#495057' : 'transparent',
                      color: calendarView === 'month' ? 'white' : '#495057',
                      border: calendarView === 'month' ? 'none' : '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '0.95rem',
                      cursor: 'pointer'
                    }}>
                    Mês
                  </button>
                  )}
                  <button
                    onClick={() => setCalendarView('week')}
                    title="Visualizar semana"
                    style={{
                      padding: '0.5rem 1rem',
                      background: calendarView === 'week' ? '#495057' : 'transparent',
                      color: calendarView === 'week' ? 'white' : '#495057',
                      border: calendarView === 'week' ? 'none' : '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '0.95rem',
                      cursor: 'pointer'
                    }}>
                    Semana
                  </button>
                  <button
                    onClick={() => setCalendarView('day')}
                    title="Visualizar dia"
                    style={{
                      padding: '0.5rem 1rem',
                      background: calendarView === 'day' ? '#495057' : 'transparent',
                      color: calendarView === 'day' ? 'white' : '#495057',
                      border: calendarView === 'day' ? 'none' : '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '0.95rem',
                      cursor: 'pointer'
                    }}>
                    Dia
                  </button>
                    <div
                      role="radiogroup"
                      aria-label="Organizar a agenda"
                      // Na cor primária, como a fila de ícones dos filtros (pedido do Hilton, 30/09)
                      style={{ display: 'inline-flex', border: `1px solid ${colors.primary}`, borderRadius: '4px', overflow: 'hidden', marginLeft: '0.25rem' }}
                    >
                      {([
                        { value: 'time', label: 'Por horário', icon: <ScheduleIcon fontSize="small" /> },
                        { value: 'professional', label: 'Por profissional', icon: <ViewColumn fontSize="small" /> },
                      ] as const).map(opt => {
                        const selected = scheduleLayout === opt.value;
                        return (
                          <Tooltip key={opt.value} title={opt.label} enterTouchDelay={300}>
                            <button
                              type="button"
                              role="radio"
                              aria-checked={selected}
                              aria-label={opt.label}
                              className="c4u-layout-option"
                              onClick={() => changeScheduleLayout(opt.value)}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '0 0.65rem',
                                minHeight: '2.35rem',
                                border: 'none',
                                background: selected ? colors.primary : 'transparent',
                                color: selected ? 'white' : colors.primaryHover,
                                cursor: 'pointer',
                                transition: 'background-color 160ms ease-out, color 160ms ease-out',
                              }}
                            >
                              {opt.icon}
                            </button>
                          </Tooltip>
                        );
                      })}
                    </div>
                  <Tooltip title="Legenda das cores de confirmação e presença">
                    <IconButton
                      onClick={(e) => setLegendAnchor(e.currentTarget)}
                      size="small"
                      aria-label="Legenda das cores"
                      sx={{ color: '#495057', border: '1px solid #ced4da', borderRadius: '4px', padding: '6px', marginLeft: '0.25rem' }}
                    >
                      <Palette fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Popover
                    open={Boolean(legendAnchor)}
                    anchorEl={legendAnchor}
                    onClose={() => setLegendAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    disableScrollLock
                  >
                    <Box sx={{ p: 2, minWidth: 240, maxWidth: 360 }}>
                      {[
                        { title: 'Confirmação', items: scheduleOptions?.confirmationStatuses ?? [] },
                        { title: 'Presença (comparecimento)', items: scheduleOptions?.presenceStatuses ?? [] },
                      ].map((group) => (
                        <Box key={group.title} sx={{ '& + &': { mt: 1.5 } }}>
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.75 }}>
                            {group.title}
                          </Typography>
                          {group.items.length === 0 && (
                            <Typography sx={{ fontSize: '0.8rem', color: '#adb5bd' }}>Sem status cadastrados</Typography>
                          )}
                          {group.items.map((st) => (
                            <Box key={st.name} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.35 }}>
                              <Box sx={{ width: 12, height: 12, borderRadius: '50%', flexShrink: 0, background: st.color || '#6c757d', border: '1px solid rgba(0,0,0,0.08)' }} />
                              <Typography sx={{ fontSize: '0.85rem', color: '#495057' }}>{st.name}</Typography>
                            </Box>
                          ))}
                        </Box>
                      ))}
                      <Typography sx={{ fontSize: '0.72rem', color: '#adb5bd', mt: 1.5 }}>
                        As cores vêm do cadastro de status da agenda (Administrativo).
                      </Typography>
                    </Box>
                  </Popover>
                </div>
              </div>
            </div>
            </div>

            {/* Grade do Calendário: continuação do bloco preso acima (sem borda e sem raio em cima) */}
            <div style={{
              border: '1px solid #e9ecef',
              borderTop: 'none',
              borderRadius: '0 0 var(--c4u-radius) var(--c4u-radius)',
              overflow: 'hidden',
              position: 'relative',
              // Visão por profissional ocupa a largura toda do painel (pedido do Hilton, 30/09)
              ...(proLayout ? {
                margin: '0 calc(-1 * var(--c4u-gap-lg)) calc(-1 * var(--c4u-gap-lg))',
                borderLeft: 'none',
                borderRight: 'none',
                borderTop: '1px solid #e9ecef',
              } : {}),
            }}>
              {/* Indicador de loading */}
              {isLoading && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(255, 255, 255, 0.8)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 10,
                  fontSize: '1.1rem',
                  color: '#03B4C6',
                  fontWeight: '500'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      border: '2px solid #03B4C6',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Atualizando agenda...
                  </div>
                </div>
              )}
              {/* Visão Mensal */}
              {calendarView === 'month' && (
                <div className="schedule-month-view" style={{ width: '100%', overflow: 'hidden' }}>
                  <div className="schedule-month-grid" style={{ width: '100%' }}>
                    {/* Cabeçalho dos dias da semana */}
                    <div className="schedule-month-header" style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(7, minmax(120px, 1fr))',
                      background: '#f8f9fa',
                      borderBottom: '1px solid #e9ecef'
                    }}>
                      {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'].map(day => (
                        <div key={day} style={{
                          padding: '0.75rem',
                          textAlign: 'center',
                          fontWeight: '600',
                          fontSize: '0.95rem',
                          color: '#495057',
                          borderRight: '1px solid #e9ecef'
                        }}>
                          {day}
                        </div>
                      ))}
                    </div>

                    {/* Dias do mês */}
                    <div className="schedule-month-days-grid" style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(7, minmax(120px, 1fr))',
                      gridAutoRows: 'minmax(140px, auto)'
                    }}>
                      {getDaysInMonth().map((date, index) => {
                        const isCurrentMonth = date.getMonth() === currentDate.getMonth();
                        const isToday = date.toDateString() === new Date().toDateString();
                        const events = getEventsForDate(date);
                        const dayBlocks = showBlocks ? getBlocksForDate(date) : [];

                        return (
                          <div key={index} className="schedule-month-day" style={{
                            padding: '0.5rem',
                            borderRight: (index + 1) % 7 !== 0 ? '1px solid #e9ecef' : 'none',
                            borderBottom: '1px solid #e9ecef',
                            background: withClosedHatch(isClosedDay(date.getDay()), isCurrentMonth ? 'white' : '#f8f9fa'),
                            position: 'relative',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden'
                          }}
                        onClick={() => openNewAppointmentModal(date)}
                        onMouseEnter={(e) => {
                          if (isCurrentMonth) {
                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (isCurrentMonth) {
                            e.currentTarget.style.backgroundColor = 'white';
                          }
                        }}>
                            <div className="schedule-month-day-number" style={{
                              fontSize: '0.9rem',
                              fontWeight: isToday ? '700' : '500',
                              color: isToday ? '#03B4C6' : isCurrentMonth ? '#495057' : '#adb5bd',
                              marginBottom: '0.25rem'
                            }}>
                              {date.getDate()}
                            </div>

                            <div style={{
                              flex: 1,
                              overflow: 'hidden',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '2px'
                            }}>
                              {dayBlocks.map(b => renderBlockChip(b, true))}
                              {events.slice(0, 5).map((event, eventIndex) => (
                                <div
                                  key={eventIndex}
                                  className="schedule-month-event"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEditModal(event);
                                  }}
                                  style={{
                                    background: statusColorOf(event),
                                    color: 'white',
                                    padding: '2px 4px',
                                    borderRadius: '2px',
                                    fontSize: '0.85rem',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    cursor: 'pointer',
                                    transition: 'opacity 0.2s',
                                    lineHeight: '1.2'
                                  }}
                                title={eventTooltip(event)}
                                onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                              >
                                {event.time} {patientDisplayName(event.patient)}
                              </div>
                            ))}
                            </div>

                          {events.length > 5 && (
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                openEventModal(date, events);
                              }}
                              style={{
                                color: '#495057',
                                fontSize: '0.8rem',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                padding: '2px 4px',
                                marginTop: '2px',
                                fontWeight: '500'
                              }}
                            >
                              +{events.length - 5} mais
                            </div>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Visão Semanal: mesma apresentação do mês (pílulas por dia, no horário) */}
              {proLayout && renderProfessionalGrid()}

              {calendarView === 'week' && !proLayout && (() => {
                const weekDays = getWeekDays();
                const eventsByDay = weekDays.map(d => getEventsForDate(d));
                const blocksByDay = weekDays.map(d => (showBlocks ? getBlocksForDate(d) : []));
                // Grade SEMPRE completa (06-23h, igual à visão diária) + horários de evento
                // fora da grade padrão — o clique em qualquer horário cria agendamento
                const eventTimes = eventsByDay.flat().map(e => e.time);
                const times = Array.from(new Set([...generateTimeSlots(), ...eventTimes])).sort();
                const gridCols = '64px repeat(7, minmax(0, 1fr))';

                return (
                  <div>
                    {/* Cabeçalho: canto vazio + dias da semana */}
                    <div style={{ display: 'grid', gridTemplateColumns: gridCols, borderBottom: '1px solid #e9ecef' }}>
                      <div style={{ background: '#f8f9fa', borderRight: '1px solid #e9ecef' }} />
                      {weekDays.map((date, dayIndex) => {
                        const isToday = date.toDateString() === new Date().toDateString();
                        return (
                          <div key={dayIndex} style={{
                            background: isToday ? '#e3f2fd' : '#f8f9fa',
                            borderRight: dayIndex < 6 ? `1px solid ${GRID_COL}` : 'none',
                            padding: '0.5rem',
                            textAlign: 'center'
                          }}>
                            <div style={{ fontSize: '0.8rem', fontWeight: '600', color: isToday ? '#1976d2' : '#495057' }}>
                              {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'][dayIndex]}
                            </div>
                            <div style={{ fontSize: '1rem', fontWeight: isToday ? '700' : '500', color: isToday ? '#1976d2' : '#495057' }}>
                              {date.getDate()}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {times.map((time) => {
                      // Mesmo padrão da visão diária: rótulo só na hora cheia; a meia hora
                      // fica como linha intermediária (mais suave), toda clicável
                      const isFullHour = time.endsWith(':00');
                      return (
                      <div key={time} style={{ display: 'grid', gridTemplateColumns: gridCols, borderBottom: isFullHour ? GRID_LINE_HALF : GRID_LINE_HOUR }}>
                        {/* Rótulo do horário (hora cheia apenas — alinhado à visão diária) */}
                        <div style={{
                          background: isFullHour ? '#f8f9fa' : 'white',
                          borderRight: '1px solid #e9ecef',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.85rem',
                          color: '#6c757d',
                          fontWeight: isFullHour ? '500' : '400'
                        }}>
                          {isFullHour ? time : ''}
                        </div>
                        {/* Uma célula por dia; até 5 pílulas + "+N mais" (clique no vazio cria) */}
                        {weekDays.map((date, dayIndex) => {
                          const slotEvents = eventsByDay[dayIndex].filter(e => e.time === time);
                          const isToday = date.toDateString() === new Date().toDateString();
                          const slotMin = timeToMinutesOf(time);
                          // Bloqueio: pílula em TODA meia hora que ele cobre, com a faixa completa — o intervalo
                          // inteiro aparece ocupado na coluna, como no dia (pedido do Hilton, 30/09)
                          const coveringHere = blocksByDay[dayIndex].filter(b => {
                            const { start, end } = blockMinutes(b);
                            return start < slotMin + 30 && end > slotMin;
                          });
                          const closedSlot = isClosedSlot(date.getDay(), slotMin);
                          const cellBg = withClosedHatch(closedSlot, isToday ? '#f5fbff' : isFullHour ? '#fafafa' : 'white');
                          return (
                            <div
                              key={dayIndex}
                              onClick={() => openNewAppointmentModal(date, time)}
                              style={{
                                borderRight: dayIndex < 6 ? `1px solid ${GRID_COL}` : 'none',
                                // Zebra igual à visão diária: hora cheia #fafafa, meia hora branca;
                                // o dia de hoje mantém o tom azulado; bloqueio hachura por cima
                                background: cellBg,
                                padding: '4px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '3px',
                                minHeight: '45px',
                                cursor: 'pointer'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.background = withClosedHatch(closedSlot, '#e3f2fd')}
                              onMouseLeave={(e) => e.currentTarget.style.background = cellBg}
                            >
                              {coveringHere.map(b => renderBlockPill(b))}
                              {slotEvents.slice(0, WEEK_SLOT_MAX_PILLS).map((event, i) => (
                                <div
                                  key={i}
                                  className="schedule-month-event"
                                  onClick={(e) => { e.stopPropagation(); openEditModal(event); }}
                                  style={{
                                    background: statusColorOf(event),
                                    color: 'white',
                                    padding: '2px 4px',
                                    borderRadius: '3px',
                                    fontSize: '0.85rem',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    cursor: 'pointer',
                                    transition: 'opacity 0.2s',
                                    lineHeight: '1.3'
                                  }}
                                  title={eventTooltip(event)}
                                  onMouseEnter={(e) => e.currentTarget.style.opacity = '0.85'}
                                  onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                >
                                  <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{event.time}</span>{' '}
                                  {patientDisplayName(event.patient)}
                                </div>
                              ))}

                              {slotEvents.length > WEEK_SLOT_MAX_PILLS && (
                                <div
                                  onClick={(e) => { e.stopPropagation(); openEventModal(date, slotEvents); }}
                                  style={{
                                    color: '#495057',
                                    fontSize: '0.8rem',
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    padding: '0 4px',
                                    fontWeight: '500'
                                  }}
                                >
                                  +{slotEvents.length - WEEK_SLOT_MAX_PILLS} mais
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Visão Diária com Grade de Horários */}
              {calendarView === 'day' && !proLayout && (
                <div>
                  {/* Cabeçalho do dia */}

                  {/* Grade de horários diária — sempre completa e clicável */}
                  <div style={{ display: 'flex', height: `${daySlots.length * 45}px`, overflow: 'hidden' }}>
                    {/* Coluna de horários */}
                    <div style={{
                      width: '100px',
                      background: '#f8f9fa',
                      borderRight: '1px solid #e9ecef'
                    }}>
                      {daySlots.map((time) => (
                        <div key={time} id={`day-slot-${time}`} style={{
                          height: '45px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.85rem',
                          color: '#6c757d',
                          borderBottom: time.endsWith(':00') ? GRID_LINE_HALF : GRID_LINE_HOUR,
                          background: time.endsWith(':00') ? '#f8f9fa' : 'white',
                          fontWeight: time.endsWith(':00') ? '500' : '400'
                        }}>
                          {time.endsWith(':00') ? time : ''}
                        </div>
                      ))}
                    </div>

                    {/* Área de eventos */}
                    <div style={{ flex: 1, position: 'relative', background: 'white' }}>
                      {/* Linhas de horário */}
                      {daySlots.map((time, timeIndex) => {
                        const closedSlot = isClosedSlot(currentDate.getDay(), timeToMinutesOf(time));
                        const slotBg = withClosedHatch(closedSlot, time.endsWith(':00') ? '#fafafa' : 'transparent');
                        return (
                          <div
                            key={time}
                            onClick={() => openNewAppointmentModal(currentDate, time)}
                            style={{
                              position: 'absolute',
                              top: `${timeIndex * 45}px`,
                              left: 0,
                              right: 0,
                              height: '45px',
                              borderBottom: time.endsWith(':00') ? GRID_LINE_HALF : GRID_LINE_HOUR,
                              background: slotBg,
                              cursor: 'pointer'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = withClosedHatch(closedSlot, '#e3f2fd')}
                            onMouseLeave={(e) => e.currentTarget.style.background = slotBg}
                          />
                        );
                      })}

                      {/* Eventos posicionados */}
                      {(() => {
                        // Bloqueios numa FAIXA PRÓPRIA à esquerda; agendamentos no restante (opção A do
                        // Hilton, 30/09). No mesmo cálculo de colunas, um bloqueio longo ficava estreito e
                        // com agendamentos desenhados por cima — a faixa mantém qualquer duração inteira.
                        const blockItems = showBlocks ? getBlocksForDate(currentDate).map(blockAsDayItem) : [];
                        const blockLayout = calculateEventLayoutPixels(blockItems) as (LayoutEvent & DayItem)[];
                        const layoutEvents = calculateEventLayoutPixels(getEventsForDate(currentDate));
                        // Faixa própria à esquerda só nos dias com bloqueio visível
                        const laneWidth = blockLayout.length > 0 ? DAY_BLOCK_LANE_PERCENT : 0;

                        if (layoutEvents.length === 0 && blockLayout.length === 0) {
                          // pointerEvents none: o aviso nunca bloqueia o clique nos horários
                          return (
                            <div style={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              textAlign: 'center',
                              color: '#6c757d',
                              fontSize: '1.2rem',
                              pointerEvents: 'none'
                            }}>
                              Nenhum agendamento para este dia
                            </div>
                          );
                        }

                        return (<>
                          {laneWidth > 0 && (
                            // Containers sem captura de clique: o horário vazio continua criando agendamento
                            <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${laneWidth}%`, pointerEvents: 'none' }}>
                              {blockLayout.map(event => renderDayBlockCard(event, event.block!))}
                            </div>
                          )}
                          <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${laneWidth}%`, right: 0, pointerEvents: 'none' }}>
                        {layoutEvents.map((event, eventIndex) => (
                          <div
                            key={eventIndex}
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditModal(event);
                            }}
                            style={{
                              pointerEvents: 'auto',
                              position: 'absolute',
                              top: event.top,
                              left: event.left,
                              width: event.width,
                              height: event.height,
                              background: statusColorOf(event),
                              color: 'white',
                              borderRadius: '8px',
                              padding: '8px 12px',
                              fontSize: '0.9rem',
                              cursor: 'pointer',
                              overflow: 'hidden',
                              zIndex: event.zIndex,
                              transition: 'all 0.2s ease',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'scale(1.02)';
                              e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.2)';
                              e.currentTarget.style.zIndex = '100';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'scale(1)';
                              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                              e.currentTarget.style.zIndex = event.zIndex.toString();
                            }}
                            title={eventTooltip(event)}
                          >
{/* Layout otimizado: linha 1: hora + paciente + duração, linha 2: procedimento + profissional */}
                            {event.duration <= 30 ? (
                              // Layout compacto para eventos curtos
                              <>
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'flex-start',
                                  alignItems: 'center',
                                  marginBottom: '1px',
                                  fontSize: '0.8rem',
                                  gap: '5px'
                                }}>
                                  <span style={{ fontWeight: '700' }}>{event.time}</span>
                                  <span style={{
                                    fontWeight: '600',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}>{patientDisplayName(event.patient)}</span>
                                  <span style={{ fontSize: '0.8rem', opacity: 0.9 }}>({event.duration}min)</span>
                                  <PackageMark event={event} compact />
                                </div>
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'flex-start',
                                  alignItems: 'center',
                                  fontSize: '0.68rem',
                                  opacity: 0.95,
                                  gap: '5px'
                                }}>
                                  <span style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}>{event.service}</span>
                                  <span style={{
                                    fontSize: '0.68rem',
                                    opacity: 0.9,
                                    whiteSpace: 'nowrap'
                                  }}>- {event.doctor.split(' ')[1] || event.doctor.split(' ')[0]}</span>
                                  <span style={{
                                    fontSize: '0.65rem',
                                    fontStyle: 'italic',
                                    opacity: 0.9,
                                    padding: '0 4px',
                                    background: 'rgba(255,255,255,0.2)',
                                    borderRadius: '4px',
                                    whiteSpace: 'nowrap',
                                    marginLeft: 'auto'
                                  }}>{statusLabelOf(event)}</span>
                                </div>
                              </>
                            ) : event.duration <= 60 ? (
                              // Layout médio para eventos padrão
                              <>
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'flex-start',
                                  alignItems: 'center',
                                  marginBottom: '2px',
                                  fontSize: '0.88rem',
                                  gap: '6px'
                                }}>
                                  <span style={{ fontWeight: '700' }}>{event.time}</span>
                                  <span style={{
                                    fontWeight: '600',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}>{patientDisplayName(event.patient)}</span>
                                  <span style={{ fontSize: '0.85rem', opacity: 0.9 }}>({event.duration}min)</span>
                                </div>
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'flex-start',
                                  alignItems: 'center',
                                  fontSize: '0.72rem',
                                  opacity: 0.95,
                                  gap: '6px'
                                }}>
                                  <span style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}>{event.service}</span>
                                  <span style={{
                                    fontSize: '0.72rem',
                                    opacity: 0.9,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                  }}>- {event.doctor.split(' - ')[0].split(' ').slice(0, 2).join(' ')}</span>
                                </div>
                                <div style={{
                                  fontSize: '0.68rem',
                                  opacity: 0.85,
                                  fontStyle: 'italic',
                                  textAlign: 'center',
                                  padding: '1px 5px',
                                  background: 'rgba(255,255,255,0.2)',
                                  borderRadius: '4px',
                                  marginTop: '2px',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}>
                                  {statusLabelOf(event)}
                                </div>
                              </>
                            ) : (
                              // Layout completo para eventos longos
                              <>
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'flex-start',
                                  alignItems: 'center',
                                  marginBottom: '4px',
                                  fontSize: '0.9rem',
                                  gap: '10px'
                                }}>
                                  <span style={{ fontWeight: '700' }}>{event.time}</span>
                                  <span style={{
                                    fontWeight: '600',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    fontSize: '0.95rem'
                                  }}>{patientDisplayName(event.patient)}</span>
                                  <span style={{ fontSize: '0.8rem', opacity: 0.9 }}>({event.duration}min)</span>
                                </div>
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'flex-start',
                                  alignItems: 'center',
                                  marginBottom: '3px',
                                  fontSize: '0.8rem',
                                  opacity: 0.95,
                                  gap: '10px'
                                }}>
                                  <span style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}>{event.service}</span>
                                  <span style={{
                                    fontSize: '0.8rem',
                                    opacity: 0.9,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                  }}>- {event.doctor.split(' - ')[0].split(' ').slice(0, 2).join(' ')}</span>
                                </div>
                                <div style={{
                                  fontSize: '0.75rem',
                                  opacity: 0.85,
                                  fontStyle: 'italic',
                                  textAlign: 'center',
                                  padding: '2px 6px',
                                  background: 'rgba(255,255,255,0.2)',
                                  borderRadius: '4px',
                                  marginTop: '2px'
                                }}>
                                  {statusLabelOf(event)}
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                          </div>
                        </>);
                      })()}
                    </div>
                  </div>
                </div>
              )}
            </div>
            </Paper>
          </Box>
        </Container>
      </Box>

      {/* Modal de eventos do dia (padrão do sistema: Dialog + cabeçalho turquesa) */}
      <Dialog
        open={isEventModalOpen}
        onClose={closeEventModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', maxHeight: '85vh' } }}
      >
        <DialogTitle sx={{ backgroundColor: colors.primary, color: colors.white, padding: '1rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography component="h3" sx={{ fontSize: '1.15rem', fontWeight: typography.fontWeight.semibold, margin: 0 }}>
            Agendamentos — {selectedDayDate?.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })}
          </Typography>
          <IconButton onClick={closeEventModal} sx={{ color: colors.white, padding: '0.25rem', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: '1rem !important' }}>
          {[...selectedDayEvents]
            .sort((a, b) => a.time.localeCompare(b.time))
            .map((event, index) => renderEventCard(event, index))}
        </DialogContent>
      </Dialog>


      {/* Modal unificado de agendamento */}
      <ScheduleBlockModal
        isOpen={blockModal.open}
        onClose={() => setBlockModal({ open: false, block: null })}
        professionals={blockModal.block
          ? (scheduleOptions?.professionals ?? []).filter(p => p.id === blockModal.block!.professionalRegistrationId).map(p => ({ id: p.id, name: p.name }))
          : blockProfessionals}
        defaults={{ professionalId: blockDefaultProfessional, date: blockModal.date, startTime: blockModal.startTime }}
        block={blockModal.block}
        onCreate={handleCreateBlock}
        onUpdate={handleUpdateBlock}
        onDelete={handleDeleteBlock}
        allowEdit={schedulePerms.canBlockEdit}
        allowDelete={schedulePerms.canBlockDelete}
        businessHours={scheduleOptions?.businessHours}
        loadBlocks={(professionalId, from, to) => scheduleBlocksService.getBlocks(from, to, professionalId)}
        loadAppointments={(professionalId, from, to) => schedulesService.findConflicts(professionalId, null, from, to)}
        emptyReason={scheduleOptions?.writeScope === 'own'
          ? 'Seu perfil só bloqueia a própria agenda, e seu usuário não tem cadastro de profissional ativo nesta clínica.'
          : 'Não há profissional ativo para bloquear.'}
      />

      <AppointmentModal
        isOpen={isAppointmentModalOpen}
        onClose={closeAppointmentModal}
        onSave={handleAppointmentSave}
        onScheduleNext={openSequelAppointmentModal}
        mode={appointmentModalMode}
        initialData={appointmentModalData}
        title={appointmentModalMode === 'create' ? 'Agendamento' : 'Editar Agendamento'}
        patientsList={patientsList}
        businessHours={scheduleOptions?.businessHours}
        professionalsList={(scheduleOptions?.professionals ?? []).filter(p => p.active).map(p => p.name)}
        teamsList={(scheduleOptions?.teams ?? []).map(t => t.name)}
        serviceTypesList={(scheduleOptions?.serviceTypes ?? []).map(s => s.name)}
        professionalOptions={(scheduleOptions?.professionals ?? []).filter(p => p.active)}
        roomOptions={scheduleOptions?.rooms ?? []}
        teamOptions={scheduleOptions?.teams ?? []}
        serviceTypeOptions={scheduleOptions?.serviceTypes ?? []}
        loadPatientDiscounts={loadPatientDiscountsByName}
        loadPatientInfo={loadPatientInfoByName}
        searchPatients={searchPatientsForModal}
        loadHolidaysOnDate={loadHolidaysOnDate}
        loadBlocks={(professionalId, from, to) => scheduleBlocksService.getBlocks(from, to, professionalId)}
        loadConflicts={({ professionalId, patient, from, to }) => schedulesService.findConflicts(
          professionalId,
          (Object.entries(patientLabelIds.current).find(([label]) => label.split(' - ')[0] === patient)?.[1]
            ?? events.find(ev => ev.patient === patient)?.patientId ?? null),
          from, to)}
        confirmationOptions={(scheduleOptions?.confirmationStatuses ?? []).map(s => s.name)}
        presenceOptions={(scheduleOptions?.presenceStatuses ?? []).map(s => s.name)}
        terminalConfirmations={(scheduleOptions?.confirmationStatuses ?? [])
          .filter(s => s.isTerminal).map(s => s.name)}
        showPricing={scheduleOptions?.dataScope !== 'own'}
        paid={appointmentModalMode === 'edit' && (selectedEvent?.paid ?? false)}
        paymentDate={appointmentModalMode === 'edit' ? (selectedEvent?.paymentDate ?? null) : null}
        paymentFinalValue={appointmentModalMode === 'edit' ? selectedEvent?.paymentFinalValue ?? null : null}
        paymentDiscount={appointmentModalMode === 'edit' ? selectedEvent?.paymentDiscount ?? null : null}
        ownProfileLock={scheduleOptions?.writeScope === 'own' || scheduleOptions?.dataScope === 'own'}
        seriesItems={appointmentModalMode === 'edit' ? appointmentSeriesItems : undefined}
        seriesPackage={appointmentModalMode === 'edit' ? appointmentSeriesPackage : null}
        currentScheduleId={appointmentModalMode === 'edit' ? selectedEvent?.id ?? null : null}
        onDelete={appointmentModalMode === 'edit' ? handleAppointmentDelete : undefined}
        canDelete={schedulePerms.canDelete}
        locked={appointmentModalMode === 'edit' && ((selectedEvent?.locked ?? false) || isForeignEvent || !schedulePerms.canOpenForChange)}
        lockedMessage={appointmentModalMode === 'edit' && isForeignEvent
          ? 'Somente leitura — agendamento de outro profissional'
          : appointmentModalMode === 'edit' && !schedulePerms.canOpenForChange
            ? 'Somente leitura — seu perfil não altera agendamentos'
            : undefined}
        attendanceOnly={appointmentModalMode === 'edit' && !schedulePerms.canEdit && schedulePerms.canAttendance}
        statusLocked={appointmentModalMode === 'edit' && !schedulePerms.canAttendance}
        rescheduleCount={appointmentModalMode === 'edit' ? selectedEvent?.numReschedule ?? 0 : 0}
        loadHistory={appointmentModalMode === 'edit' && selectedEvent
          ? () => schedulesService.getHistory(selectedEvent.id)
          : undefined}
        loadHolidayCalendar={loadHolidayCalendar}
        onOpenOccurrence={appointmentModalMode === 'edit' ? openOccurrenceById : undefined}
        onRescheduleSeries={appointmentModalMode === 'edit' && schedulePerms.canEdit ? handleRescheduleSeries : undefined}
      />

      {/* Modal de agenda do paciente (mesmo padrão do modal do "+") */}
      <Dialog
        open={isPatientScheduleModalOpen}
        onClose={closePatientScheduleModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', maxHeight: '85vh' } }}
      >
        <DialogTitle sx={{ backgroundColor: colors.primary, color: colors.white, padding: '1rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography component="h3" sx={{ fontSize: '1.15rem', fontWeight: typography.fontWeight.semibold, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Agenda: {selectedPatients.length > 0 ? patientDisplayName(selectedPatients[0].split(' - ')[0]) : ''}
          </Typography>
          <IconButton onClick={closePatientScheduleModal} sx={{ color: colors.white, padding: '0.25rem', flexShrink: 0, '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: '1rem !important' }}>
          {patientScheduleEvents.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d', fontSize: '1rem' }}>
              Nenhum agendamento encontrado para este paciente.
            </div>
          ) : (
            [...patientScheduleEvents]
              .sort((a, b) => (a.date === b.date ? a.time.localeCompare(b.time) : a.date.localeCompare(b.date)))
              .map((event, index) => renderEventCard(event, index, formatDate(event.date)))
          )}
        </DialogContent>
      </Dialog>

      <FooterInternal
        className="login-footer-component"
      />

      {/* Cadastro rápido de paciente (componente reutilizável — mesmo da lista) */}
      <QuickPatientRegisterModal
        open={isQuickRegisterOpen}
        onClose={() => setIsQuickRegisterOpen(false)}
        onSuccess={handleQuickRegisterSuccess}
      />

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </div>
  );
};

export default ProfessionalSchedule;